import React, { useEffect, useRef, useState, useCallback } from 'react';
import supabase from '../config/supabase';
import ImportProcessingModal from './ImportProcessingModal';
import VendorAutocompleteInput from './VendorAutocompleteInput';
import ImportApprovalTable from './ImportApprovalTable';
import { formatErrorMessage } from '../utils/formatErrorMessage';
import {
  normalizeVendorName
} from '../utils/vendorResolution';
import { classifyImportItems, overrideItemClassification, dedupeBatchItems } from '../utils/importDedup';
import { selectInventorySheetName } from '../utils/excelSheetSelection';
import { takeScanPhoto } from '../utils/scanPhoto';
import { findCountTargetsForNewItem, assignItemToCountSessions } from '../utils/autoAssignToCounts';
import { recordPriceHistory } from '../utils/priceHistory';
import { visuallyHiddenFileInputStyle } from '../utils/visuallyHiddenFileInputStyle';
import { fileSignature } from '../utils/fileDedupe';
import StagedFilePreview from './StagedFilePreview';

// Claude AI Configuration
const CONFIG = {
  CLAUDE_MODEL: 'claude-sonnet-4-20250514', // Claude Sonnet 4 (latest version)
  MAX_TOKENS: 64000, // Maximum output tokens for Claude Sonnet 4
  TEMPERATURE: 0.2,
  API_URL: 'http://localhost:3011/api/claude', // Use local CORS proxy
  API_VERSION: '2023-06-01'
};

const CAMERA_IMAGE_TYPES = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];
const IMPORT_FILE_TYPES = ['.csv', '.xlsx', '.xls', '.txt', '.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];
const MAX_ITEMS_PER_DOCUMENT = 300;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const IMAGE_MAX_DIMENSION = 2048;
const IMAGE_JPEG_QUALITY = 0.82;
const MAX_IMAGES_PER_IMPORT_BATCH = 5;
const MAX_RESIZED_IMAGE_BYTES = 3 * 1024 * 1024;
const MAX_TOTAL_IMAGE_BYTES = 8 * 1024 * 1024;

const importProgressMessage = (progress) => {
  const itemCount = Number(progress?.extracted_items_so_far || 0);
  const countLabel = itemCount > 0
    ? `${itemCount} imported product${itemCount === 1 ? '' : 's'}`
    : 'your imported products';
  const messages = {
    saving_inventory_items: `Saving ${countLabel}…`,
    saving_vendor_products: 'Saving vendor packages and prices…',
    naming_inventory_items: 'Choosing clear inventory names…',
    comparing_prices: 'Comparing package sizes and objective prices…',
    screening_related_products: `Screening ${countLabel} for possible matches…`,
    matching_related_products: `Finding related products across ${countLabel}…`,
    preparing_import_review: 'Preparing related products for your review…',
  };
  return messages[progress?.status] || null;
};

const isImageByName = (fileName) => {
  const lower = String(fileName || '').toLowerCase();
  return CAMERA_IMAGE_TYPES.some((extension) => lower.endsWith(extension));
};

const getImageMimeFromName = (fileName) => {
  const lowerName = (fileName || '').toLowerCase();
  if (lowerName.endsWith('.png')) return 'image/png';
  if (lowerName.endsWith('.webp')) return 'image/webp';
  if (lowerName.endsWith('.heic')) return 'image/heic';
  if (lowerName.endsWith('.heif')) return 'image/heif';
  return 'image/jpeg';
};

const FileUpload = ({ onUpload, onCancel, currentStore: propCurrentStore, uploadMode = 'import', initialFiles = null }) => {
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const preImportCatalogSnapshotRef = useRef([]);
  const [uploading, setUploading] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importResultErrors, setImportResultErrors] = useState([]);
  const [openResultSection, setOpenResultSection] = useState(null);
  const [detailedStatus, setDetailedStatus] = useState([]);
  const [currentStore, setCurrentStore] = useState(propCurrentStore);
  const lastFetchedStoreIdRef = useRef(null);
  const [processedItems, setProcessedItems] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [countTargetsMap, setCountTargetsMap] = useState({});
  const [fileName, setFileName] = useState('');
  const [existingStoreData, setExistingStoreData] = useState({
    categories: [],
    locations: [],
    vendorsByCategory: {}
  });
  const [progressInfo, setProgressInfo] = useState({
    currentChunk: 0,
    totalChunks: 0,
    currentItem: 0,
    totalItems: 0,
    percentage: 0
  });
  const isCameraMode = uploadMode === 'camera';
  const allowedFileExtensions = isCameraMode ? [...CAMERA_IMAGE_TYPES, '.pdf'] : IMPORT_FILE_TYPES;
  const maxSelectableFiles = isCameraMode ? 10 : 20;

  // Import processing modal state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importClassification, setImportClassification] = useState(null);
  const [importFeedItems, setImportFeedItems] = useState([]);
  const [importStatusText, setImportStatusText] = useState('');
  const [importProgressPct, setImportProgressPct] = useState(0);
  const [importItemsProcessed, setImportItemsProcessed] = useState(0);
  const [importTotalEstimated, setImportTotalEstimated] = useState(0);
  const [importError, setImportError] = useState(null);
  const [importCompletionSummary, setImportCompletionSummary] = useState(null);
  const [importReadyForReview, setImportReadyForReview] = useState(false);
  const [importSessionSummary, setImportSessionSummary] = useState(null);
  const [importLastFileCount, setImportLastFileCount] = useState(0);
  const [importFileResults, setImportFileResults] = useState([]);
  const [importFileProgress, setImportFileProgress] = useState(null);
  const summaryTimerRef = useRef(null);
  const showOrderGuideWarning = false;
  const [deferredReviewNotice, setDeferredReviewNotice] = useState(null);

  useEffect(() => {
    loadUserAndStore();
  }, []);

  useEffect(() => {
    return () => {
      if (summaryTimerRef.current) { clearTimeout(summaryTimerRef.current); summaryTimerRef.current = null; }
    };
  }, []);

  useEffect(() => {
    if (propCurrentStore) {
      setCurrentStore(propCurrentStore);
    }
  }, [propCurrentStore]);

  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      addFilesToSelection(initialFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentStore?.id) {
      lastFetchedStoreIdRef.current = null;
      return;
    }
    if (lastFetchedStoreIdRef.current === currentStore.id) return;
    lastFetchedStoreIdRef.current = currentStore.id;
    fetchExistingStoreData(); // Fetch once per store id to avoid duplicate mobile fetches
  }, [currentStore?.id]);

  // Helper function to add detailed status updates
  const addDetailedStatus = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDetailedStatus(prev => [...prev.slice(-4), { message, type, timestamp }]); // Keep last 5 messages
  };

  useEffect(() => {
    const shouldLockPage = importModalVisible && aiProcessing;
    if (!shouldLockPage) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyCursor = document.body.style.cursor;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.cursor = 'progress';
    document.body.classList.add('items-import-page-locked');

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.cursor = previousBodyCursor;
      document.body.classList.remove('items-import-page-locked');
    };
  }, [importModalVisible, aiProcessing]);

  // Load user data
  const loadUserAndStore = async () => {
    try {
      // Reserved for future per-user import behavior.
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes} B`;
  };

  const fetchCatalogRowsForStore = async (storeId, selectClause) => {
    const pageSize = 1000;
    let from = 0;
    const rows = [];

    while (true) {
      const to = from + pageSize - 1;
      const { data, error } = await supabase
        .from('inventory_items')
        .select(selectClause)
        .eq('store_id', storeId)
        .order('id', { ascending: true })
        .range(from, to);

      if (error) throw error;
      const batch = data || [];
      rows.push(...batch);
      if (batch.length < pageSize) break;
      from += pageSize;
    }

    return rows;
  };

  const isAcceptedFileType = (file) => {
    const lowerName = String(file?.name || '').toLowerCase();
    return allowedFileExtensions.some((extension) => lowerName.endsWith(extension));
  };

  const addFilesToSelection = (incomingFiles) => {
    const incoming = Array.from(incomingFiles || []);
    if (!incoming.length) return;

    let rejectedType = 0;
    let rejectedSize = 0;
    let rejectedLimit = 0;
    let rejectedImageCap = 0;

    setSelectedFiles((previousFiles) => {
      const next = [...previousFiles];
      const signatures = new Set(previousFiles.map(fileSignature));
      let imageCount = previousFiles.filter((file) => isImageByName(file.name) || String(file.type || '').startsWith('image/')).length;

      incoming.forEach((file) => {
        const signature = fileSignature(file);
        if (signatures.has(signature)) return;
        if (!isAcceptedFileType(file)) {
          rejectedType += 1;
          return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          rejectedSize += 1;
          return;
        }
        if (next.length >= maxSelectableFiles) {
          rejectedLimit += 1;
          return;
        }
        const isImage = isImageByName(file.name) || String(file.type || '').startsWith('image/');
        if (isImage && imageCount >= MAX_IMAGES_PER_IMPORT_BATCH) {
          rejectedImageCap += 1;
          return;
        }
        if (isImage) imageCount += 1;
        signatures.add(signature);
        next.push(file);
      });

      return next;
    });

    if (rejectedType || rejectedSize || rejectedLimit || rejectedImageCap) {
      const notes = [];
      if (rejectedType) notes.push(`${rejectedType} unsupported`);
      if (rejectedSize) notes.push(`${rejectedSize} over 10MB`);
      if (rejectedLimit) notes.push(`${rejectedLimit} over file limit`);
      if (rejectedImageCap) notes.push(`${rejectedImageCap} over ${MAX_IMAGES_PER_IMPORT_BATCH}-image cap`);
      setProcessingStatus(`Some files were skipped (${notes.join(', ')}).`);
    }
  };

  const handleFileInputChange = (event) => {
    addFilesToSelection(event.target.files);
    event.target.value = '';
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsDragOver(false);
  };

  const removeSelectedFile = (indexToRemove) => {
    setSelectedFiles((previousFiles) => previousFiles.filter((_, index) => index !== indexToRemove));
  };

  const isImageFile = (file) => {
    if (!file) return false;
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    return type.startsWith('image/') || CAMERA_IMAGE_TYPES.some((extension) => name.endsWith(extension));
  };

  const resizeImageForUpload = async (file) => {
    const fallback = {
      blob: file,
      mediaType: (file.type || '').trim() || getImageMimeFromName(file.name),
      resized: false,
    };
    if (typeof createImageBitmap !== 'function') return fallback;
    try {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      const longestEdge = Math.max(width, height);
      const scale = Math.min(1, IMAGE_MAX_DIMENSION / longestEdge);
      if (scale === 1 && (file.size || 0) <= MAX_RESIZED_IMAGE_BYTES) {
        if (typeof bitmap.close === 'function') bitmap.close();
        return fallback;
      }
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, targetW, targetH);
      if (typeof bitmap.close === 'function') bitmap.close();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', IMAGE_JPEG_QUALITY));
      if (!blob) return fallback;
      return { blob, mediaType: 'image/jpeg', resized: true };
    } catch (err) {
      console.warn('⚠️ Image resize failed, using original file:', err);
      return fallback;
    }
  };

  const readImageFile = async (file) => {
    const { blob, mediaType } = await resizeImageForUpload(file);
    if ((blob.size || 0) > MAX_RESIZED_IMAGE_BYTES) {
      throw new Error(`${file.name} is still too large after resize (${(blob.size / 1024 / 1024).toFixed(1)} MB). Try a smaller or lower-resolution image.`);
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result;
        if (typeof result !== 'string' || !result.includes(',')) {
          reject(new Error('Unable to read image data'));
          return;
        }

        const data = result.split(',')[1];
        resolve({ mediaType, data });
      };
      reader.onerror = () => reject(new Error('Error reading image file'));
      reader.readAsDataURL(blob);
    });
  };

  // Preprocess file to clean CSV format (like inventories-clean.html)
  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith('.csv')) {
        // Handle CSV files with Papa Parse
        if (typeof Papa !== 'undefined') {
          Papa.parse(file, {
            header: false,
            skipEmptyLines: true,
            complete: function(results) {
              console.log('📊 CSV parsed:', results.data.length, 'rows');
              const cleanedData = cleanCSVData(results.data);
              resolve(cleanedData);
            },
            error: function(error) {
              reject(new Error('CSV parsing error: ' + error.message));
            }
          });
        } else {
          // Fallback to text reading if Papa Parse not available
          const reader = new FileReader();
          reader.onload = function(e) { resolve(e.target.result); };
          reader.onerror = () => reject(new Error('Error reading CSV file'));
          reader.readAsText(file);
        }
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // Handle Excel files with SheetJS
        if (typeof XLSX !== 'undefined') {
          const reader = new FileReader();
          reader.onload = function(e) {
            try {
              const data = new Uint8Array(e.target.result);
              const workbook = XLSX.read(data, { type: 'array' });
              const sheetName = selectInventorySheetName(workbook);
              if (!sheetName) {
                reject(new Error('Could not find a usable worksheet in this Excel file.'));
                return;
              }
              const sheet = workbook.Sheets[sheetName];
              const csvData = XLSX.utils.sheet_to_csv(sheet, { skipHidden: false });

              console.log(`📊 Excel sheets: [${workbook.SheetNames.join(', ')}] → selected "${sheetName}"`);
              console.log('📊 Excel converted to CSV, length:', csvData.length);

              // Parse the CSV data to clean it
              if (typeof Papa !== 'undefined') {
                Papa.parse(csvData, {
                  header: false,
                  skipEmptyLines: true,
                  complete: function(results) {
                    const cleanedData = cleanCSVData(results.data);
                    resolve(cleanedData);
                  },
                  error: function(error) {
                    reject(new Error('Excel CSV conversion error: ' + error.message));
                  }
                });
              } else {
                resolve(csvData);
              }
            } catch (error) {
              reject(new Error('Excel processing error: ' + error.message));
            }
          };
          reader.onerror = () => reject(new Error('Error reading Excel file'));
          reader.readAsArrayBuffer(file);
        } else {
          reject(new Error('Excel support requires SheetJS library'));
        }
      } else if (fileName.endsWith('.pdf')) {
        // Handle PDF files with PDF.js
        if (typeof pdfjsLib !== 'undefined') {
          const reader = new FileReader();
          reader.onload = async function(e) {
            try {
              console.log('📄 Processing PDF file...');
              
              // Configure PDF.js worker
              pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
              
              const pdfData = new Uint8Array(e.target.result);
              const pdf = await pdfjsLib.getDocument(pdfData).promise;
              
              console.log('📊 PDF loaded, pages:', pdf.numPages);
              
              let allText = '';
              
              // Extract text from all pages
              for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();
                
                // Extract text items and try to preserve structure
                let pageText = '';
                let currentY = null;
                let currentLine = '';
                
                textContent.items.forEach(item => {
                  // If we're on a new line (different Y position)
                  if (currentY !== null && Math.abs(item.transform[5] - currentY) > 5) {
                    if (currentLine.trim()) {
                      pageText += currentLine.trim() + '\n';
                    }
                    currentLine = '';
                  }
                  
                  currentY = item.transform[5];
                  currentLine += item.str + ' ';
                });
                
                // Add the last line
                if (currentLine.trim()) {
                  pageText += currentLine.trim() + '\n';
                }
                
                allText += pageText + '\n';
              }
              
              console.log('📊 PDF text extracted, length:', allText.length);
              
              // Convert PDF text to CSV format
              const csvData = convertPdfTextToCsv(allText);
              
              // If we got CSV data, clean it like other CSV files
              if (csvData && csvData.includes(',')) {
                console.log('📊 PDF converted to CSV, now cleaning...');
                // Parse and clean the CSV data like we do for regular CSV files
                if (typeof Papa !== 'undefined') {
                  Papa.parse(csvData, {
                    header: false,
                    skipEmptyLines: true,
                    complete: function(results) {
                      const cleanedData = cleanCSVData(results.data);
                      resolve(cleanedData);
                    },
                    error: function(error) {
                      console.error('PDF CSV cleaning error:', error);
                      resolve(csvData); // Fallback to unclean data
                    }
                  });
                } else {
                  resolve(csvData);
                }
              } else {
                console.log('📝 No CSV structure detected, using raw text');
                resolve(csvData);
              }
              
            } catch (error) {
              console.error('PDF processing error:', error);
              reject(new Error('PDF processing error: ' + error.message));
            }
          };
          reader.onerror = () => reject(new Error('Error reading PDF file'));
          reader.readAsArrayBuffer(file);
        } else {
          reject(new Error('PDF support requires PDF.js library'));
        }
      } else {
        // Handle text files as plain text
        const reader = new FileReader();
        reader.onload = function(e) {
          resolve(e.target.result);
        };
        reader.onerror = () => reject(new Error('Error reading text file'));
        reader.readAsText(file);
      }
    });
  };

  // Clean CSV data by removing empty rows and formatting (like inventories-clean.html)
  const cleanCSVData = (data) => {
    console.log('🧹 Cleaning CSV data, original rows:', data.length);

    // Remove completely empty rows
    const nonEmptyRows = data.filter(row =>
      row.some(cell => cell && cell.toString().trim() !== '')
    );

    // Convert back to CSV format
    let cleanedCSV;
    if (typeof Papa !== 'undefined') {
      cleanedCSV = Papa.unparse(nonEmptyRows, {
        skipEmptyLines: true
      });
    } else {
      // Fallback manual CSV formatting
      cleanedCSV = nonEmptyRows.map(row => 
        row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(',')
      ).join('\n');
    }

    console.log('🧹 Cleaned CSV data, final rows:', nonEmptyRows.length);
    console.log('🧹 Sample cleaned data:', cleanedCSV.substring(0, 200) + '...');

    return cleanedCSV;
  };

  // Convert PDF text to CSV format by detecting tabular data
  const convertPdfTextToCsv = (pdfText) => {
    console.log('🔄 Converting PDF text to CSV format...');
    console.log('📄 PDF text sample:', pdfText.substring(0, 500) + '...');
    
    const lines = pdfText.split('\n').filter(line => line.trim());
    const csvRows = [];
    let headerDetected = false;
    
    // More aggressive table detection
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip obviously non-tabular lines but be less restrictive
      if (line.length < 3 || 
          line.toLowerCase().includes('page ') ||
          line.toLowerCase().includes('copyright') ||
          line.toLowerCase().includes('printed') ||
          /^[^a-zA-Z0-9]*$/.test(line) ||
          /^[\d\s\-]+$/.test(line)) { // Skip lines with only numbers, spaces, dashes
        continue;
      }
      
      let columns = [];
      let isTableRow = false;
      
             // Method 1: Look for obvious inventory/product lines first (expanded for liquor)
       if (/\b(wine|beer|spirit|bottle|case|vintage|grape|red|white|cabernet|chardonnay|pinot|merlot|sauvignon|vodka|whiskey|bourbon|rum|gin|tequila|scotch|brandy|cognac|liqueur|schnapps)/i.test(line)) {
         isTableRow = true;
         // Try multiple splitting strategies for wine/inventory data
         if (line.includes('  ')) {
           columns = line.split(/\s{2,}/).map(col => col.trim()).filter(col => col);
         } else if (/\d{4}/.test(line)) { // Year patterns
           columns = line.split(/(\d{4})/).filter(col => col.trim());
         } else if (/\$[\d,]+\.?\d*/.test(line)) { // Has price
           columns = line.split(/(\$[\d,]+\.?\d*)/).filter(col => col.trim());
         } else {
           columns = [line]; // Keep as single description
         }
       }
      
      // Method 2: Split by multiple spaces (most common in PDFs) 
      else if (line.includes('  ')) {
        columns = line.split(/\s{2,}/).map(col => col.trim()).filter(col => col);
        isTableRow = columns.length > 1;
      }
      
      // Method 3: Split by tabs
      else if (line.includes('\t')) {
        columns = line.split('\t').map(col => col.trim()).filter(col => col);
        isTableRow = columns.length > 1;
      }
      
      // Method 4: Detect price patterns and split around them
      else if (/\$[\d,]+\.?\d*/.test(line)) {
        columns = line.split(/(\$[\d,]+\.?\d*)/).filter(col => col.trim());
        isTableRow = true;
      }
      
      // Method 5: Look for quantity patterns
      else if (/\d+\s*(lbs?|kg|box|case|each|ea|pc|pcs|oz|lb|btl|bottle)/i.test(line)) {
        columns = line.split(/(\d+\s*(?:lbs?|kg|box|case|each|ea|pc|pcs|oz|lb|btl|bottle))/i).filter(col => col.trim());
        isTableRow = true;
      }
      
      // Method 6: Header detection
      else if (/\b(item|product|description|name|price|qty|quantity|unit|location|category|vintage|varietal|region|appellation)/i.test(line)) {
        headerDetected = true;
        columns = [line];
        isTableRow = true;
      }
      
      // Method 7: Lines with mixed letters and numbers (likely data)
      else if (/[a-zA-Z].*\d|\d.*[a-zA-Z]/.test(line) && line.length > 10) {
        columns = [line];
        isTableRow = true;
      }
      
      // Add the row if it looks like table data
      if (isTableRow && columns.length > 0) {
        // Clean up columns
        const cleanedColumns = columns.map(col => {
          return col.replace(/["""'']/g, '"').trim();
        }).filter(col => col.length > 0);
        
        if (cleanedColumns.length > 0) {
          csvRows.push(cleanedColumns);
        }
      }
    }
    
    console.log('📊 Detected', csvRows.length, 'potential table rows');
    console.log('📋 Sample rows:', csvRows.slice(0, 10));
    console.log('📋 Last rows:', csvRows.slice(-5));
    
    // If we found table-like data, convert to CSV
    if (csvRows.length > 2) { // Need at least 3 rows to be worth it
      let csvContent = '';
      if (typeof Papa !== 'undefined') {
        csvContent = Papa.unparse(csvRows, { skipEmptyLines: true });
      } else {
        // Fallback manual CSV creation
        csvContent = csvRows.map(row => 
          row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(',')
        ).join('\n');
      }
      
      console.log('📊 Converted PDF to CSV, length:', csvContent.length);
      console.log('📋 Sample CSV data:', csvContent.substring(0, 300) + '...');
      
      return csvContent;
    } else {
      // If no table detected, return the raw text for GPT to process
      console.log('⚠️ No table structure detected, returning raw text for AI processing');
      return pdfText;
    }
  };

  // Fetch existing categories, locations, and patterns from current store
  const fetchExistingStoreData = async () => {
    if (!currentStore?.id) return;

    try {
      console.log('📊 Fetching existing store data for smart category/location matching...');
      
      // First, get the store's configured locations and categories from store_details
      const { data: storeDetails, error: storeDetailsError } = await supabase
        .from('store_details')
        .select('locations, accounting_categories')
        .eq('store_id', currentStore.id)
        .single();

      // Get existing inventory items for vendor patterns
      const { data: existingItems, error: itemsError } = await supabase
        .from('inventory_items')
        .select('category, location, vendor, description')
        .eq('store_id', currentStore.id)
        .not('category', 'is', null)
        .not('location', 'is', null);

      if (itemsError) {
        console.error('Error fetching existing store data:', itemsError);
        return;
      }

      // Use configured locations from store_details, fallback to existing inventory locations
      let configuredLocations = [];
      let configuredCategories = [];
      
      if (storeDetails && !storeDetailsError) {
        configuredLocations = storeDetails.locations || [];
        configuredCategories = storeDetails.accounting_categories || [];
        console.log('📊 Found configured store details:', { locations: configuredLocations, categories: configuredCategories });
      }

      // Extract unique categories and locations from existing inventory as fallback
      const inventoryCategories = [...new Set(existingItems.map(item => item.category).filter(Boolean))];
      const inventoryLocations = [...new Set(existingItems.map(item => item.location).filter(Boolean))];
      
      // Combine configured and inventory data, prioritizing configured
      const categories = [...new Set([...configuredCategories, ...inventoryCategories])];
      const locations = [...new Set([...configuredLocations, ...inventoryLocations])];
      
      // Build vendor patterns by category for smarter assignment
      const vendorsByCategory = {};
      existingItems.forEach(item => {
        if (item.category && item.vendor) {
          if (!vendorsByCategory[item.category]) {
            vendorsByCategory[item.category] = new Set();
          }
          vendorsByCategory[item.category].add(item.vendor);
        }
      });

      // Convert Sets to Arrays for JSON serialization
      Object.keys(vendorsByCategory).forEach(category => {
        vendorsByCategory[category] = [...vendorsByCategory[category]];
      });

      const storeData = {
        categories: categories.sort(),
        locations: locations.sort(),
        vendorsByCategory
      };

      console.log('📊 Store data fetched:', storeData);
      setExistingStoreData(storeData);
      
    } catch (error) {
      console.error('Error fetching store data:', error);
    }
  };

  // Build comprehensive prompt for Claude
  const buildInventoryPrompt = (fileContent, chunkInfo = '') => {
    // Use existing store data if available, fallback to defaults
    const storeInfo = {
      name: currentStore?.name || 'Restaurant',
      locations: existingStoreData.locations.length > 0 
        ? existingStoreData.locations 
        : ['Walk-in Cooler', 'Walk-in Freezer', 'Kitchen Prep Area', 'Kitchen Line', 'Dry Storage', 'Bar Area'],
      categories: existingStoreData.categories.length > 0 
        ? existingStoreData.categories 
        : ['Food', 'Beverage', 'Supplies', 'Cleaning', 'Paper Goods'],
      vendorsByCategory: existingStoreData.vendorsByCategory
    };

    let prompt = `Convert various formats of restaurant inventory forms—including PDF tables with complex layouts, vendor-provided sheets, homemade forms, or handwritten (potentially scanned or OCRed) documents—into structured database records that match the schema of the \`public.inventory_items\` table.

**CRITICAL MULTI-LINE ITEM DETECTION**: This data comes from PDF extraction which may have split single inventory items across multiple rows. Before processing, carefully examine the data for split items:

**Signs of split items:**
- Incomplete data in a row (missing price, quantity, or description end)
- Next row starts with continuation text (units like "CASE", "BOTTLE", prices like "$84.00")
- Rows with only numbers, prices, or units without item names
- Inconsistent formatting suggesting line breaks occurred mid-item

**Examples of what to look for:**
- Split items: "147963 Cabernet..." + "CASE" + "$84.00 $77.00" should be merged
- Concatenated items: "150164 Pinot...119.00150339 Pinot Noir" should be split
- Missing line breaks between complete inventory entries
- Incomplete pricing or quantity data suggesting truncation

**PROCESSING STEPS:**
1. **First, scan for and merge split items** - look for incomplete rows followed by continuation data
2. **Then extract ALL inventory items** including quantities, prices, and numerical data
3. Look for patterns like:
   - Item names/descriptions  
   - Quantities (numbers followed by units)
   - Prices (dollar amounts or numerical values)
   - Categories or sections
   - Storage locations

**Store Context & Smart Matching:**
- Store Name: ${storeInfo.name}
- **EXISTING Categories** (USE THESE FIRST): ${storeInfo.categories.join(', ')}
- **EXISTING Locations** (USE THESE FIRST): ${storeInfo.locations.join(', ')}
${Object.keys(storeInfo.vendorsByCategory).length > 0 ? `- **Vendor Patterns by Category**: ${JSON.stringify(storeInfo.vendorsByCategory, null, 2)}` : ''}

**🚨 CRITICAL LOCATION PRIORITY RULES:**
1. **ONLY USE LOCATIONS FROM THE EXISTING LIST ABOVE** - NEVER create new locations
2. **FIRST PRIORITY**: Use the MOST SPECIFIC location from the existing locations list that matches the item type
3. **SECOND PRIORITY**: Use the most appropriate general location from the existing list
4. **NEVER default to the first location** - always analyze the item type first
5. **Think like a restaurant manager** - where would this item typically be stored in THIS restaurant?

**EXAMPLE**: If "Meat Locker" exists in the locations list, ALL meat products should use it over "Walk-in Cooler" or "Walk-in Freezer"

**🚨 CRITICAL**: You can ONLY assign locations that are in the existing locations list above. Do NOT create new location names.

**Smart Category Matching Examples:**
- Butter/Milk/Cheese/Eggs → Use existing "Dairy" (not "Food" or "Beverage")
- Wine/Beer/Liquor → Use existing "Alcohol" or "Beverage" 
- Paper towels/napkins → Use existing "Paper Goods" or "Supplies"
- Cleaning supplies → Use existing "Cleaning" (not "Supplies")

**INTELLIGENT CATEGORY & LOCATION MATCHING GUIDELINES**

When analyzing each item, use its name and context to infer the correct category and find a matching storage location. Use the rules below to guide classification:

🔑 **Match categories and locations using keyword patterns:**

**Meat**
- Keywords: beef, pork, chicken, lamb, sausage, steak, brisket
- Locations: "Meat Locker", "Meat Storage", "Butcher Room", "Walk-in Freezer"

**Dairy** (includes eggs — eggs require refrigeration and should NEVER go in Pantry/Dry Storage)
- Keywords: milk, cheese, butter, cream, yogurt, eggs, egg
- Locations: "Dairy Cooler", "Walk-in Cooler", "Walk-in Fridge", "Dairy Storage"

**Seafood**
- Keywords: shrimp, salmon, fish, cod, scallop, crab
- Locations: "Seafood Freezer", "Meat Locker", "Walk-in Freezer"

**Non-Alcoholic Beverage**
- Keywords: juice, soda, tea, water, sparkling, kombucha
- Locations: "Beverage Cooler", "Dry Storage", "Bar Area"

**Liquor**
- Keywords: vodka, whiskey, rum, tequila, gin, brandy
- Locations: "Liquor Storage", "Bar Area"

**Wine**
- Keywords: cabernet, chardonnay, merlot, pinot, zinfandel
- Locations: "Wine Cellar", "Bar Area"

**Beer**
- Keywords: lager, ale, IPA, pilsner, stout, beer
- Locations: "Beer Cooler", "Bar Area"

**Coffee**
- Keywords: coffee, espresso, decaf, roast
- Locations: "Coffee Storage", "Dry Storage", "Bar Area"

**Produce**
- Keywords: lettuce, tomato, onion, apple, berry, melon, greens
- Locations: "Produce Cooler", "Walk-in Cooler", "Dry Storage" (if shelf-stable)

**Baked Goods**
- Keywords: bread, buns, rolls, pastries, muffins, cake
- Locations: "Bakery Storage", "Dry Storage", "Pantry"

**Retail**
- Keywords: merchandise, retail, packaged snacks, bottled goods
- Locations: "Retail Shelf", "Dry Storage", "Retail Display"

🧠 **Matching Logic:**
1. **Identify the item's core type** (e.g., "chicken breast" → meat)
2. **FIRST**: Look for the MOST SPECIFIC location from the existing locations list that matches the item type
3. **SECOND**: If no specific location exists, use the most appropriate general location from the existing list
4. **NEVER**: Default to the first location in the list - always analyze the item type first
5. **Use best judgment if the item fits multiple categories** — prioritize food safety and usage context

**🚨 CRITICAL: NEVER default to the first location in the list. Always analyze the item type first!**

**🎯 LOCATION SELECTION PRIORITY:**
1. **MOST SPECIFIC** location from existing list (e.g., "Meat Locker" for meat)
2. **APPROPRIATE GENERAL** location from existing list (e.g., "Walk-in Freezer" for meat if no "Meat Locker")
3. **NEVER** just pick the first location without analysis

🔍 **LOCATION SELECTION RULE: Match items to the **most semantically specific** location name that fits the product.**

- For example:
  - "Cheddar Cheese" → "Cheese Room" (more specific than "Walk-in Cooler")
  - "Cabernet Sauvignon" → "Wine Cellar" (better than "Liquor Storage" or "Bar Area")
  - "Beef Brisket" → "Meat Locker" (better than "Walk-in Freezer" if both exist)
  - "Cleaning Spray" → "Janitorial Closet" (better than "Dry Storage")

❗ **If multiple valid locations exist:**
  - Choose the one **most uniquely intended** for that item type.
  - Avoid generic locations if a more targeted one exists.
  - **Think about what the location name implies.**

If unsure between two, pick the one whose name most clearly signals the item type (e.g., "Wine" → "Wine Cellar", not "Liquor Storage").

**SPECIFIC EXAMPLE - "Meat Locker":**
If "Meat Locker" exists in the existing locations list, ALL meat products should use it:
- "Beef Brisket" → "Meat Locker" ✅ (from existing locations list)
- "Pork Shoulder" → "Meat Locker" ✅ (from existing locations list)
- "Chicken Breast" → "Meat Locker" ✅ (from existing locations list)
- "Ground Beef" → "Meat Locker" ✅ (from existing locations list)
- "Salmon Fillets" → "Meat Locker" ✅ (from existing locations list)

**NOT**: "Walk-in Cooler" (even if it's first in the existing locations list) ❌

**INTELLIGENT DECISION PROCESS:**
1. **ANALYZE the item description** to identify product type (meat, dairy, alcohol, etc.)
2. **For Categories**: Check if existing category matches → use exact existing name
3. **For Locations**: 
   - **FIRST**: Look for the MOST SPECIFIC location that matches the item type
   - **SECOND**: If no specific location, use the most appropriate general location
   - **NEVER**: Default to the first location in the list
4. **Only create new category/location** if no reasonable existing match

**EXAMPLE DECISION FLOWS:**
- Item: "Beef Brisket"
- Analysis: Meat product requiring cold storage
- Location Choice: "Meat Locker" (if available) → "Walk-in Freezer" (if no Meat Locker) → "Walk-in Cooler" (last resort)
- **NOT**: "Walk-in Cooler" (first in list)

- Item: "Whole Milk"
- Analysis: Dairy product requiring refrigeration
- Location Choice: "Dairy Cooler" (if available) → "Walk-in Cooler" (if no Dairy Cooler) → "Kitchen Prep Area" (last resort)

- Item: "Cabernet Wine"
- Analysis: Alcoholic beverage
- Location Choice: "Bar Area" (if available) → "Wine Cellar" (if available) → "Liquor Storage" (if available)

- Item: "Paper Towels"
- Analysis: Paper goods, non-perishable
- Location Choice: "Paper Storage" (if available) → "Dry Storage" (if no Paper Storage)

**Database Schema:**
\`\`\`sql
CREATE TABLE public.inventory_items (
  id bigserial not null,
  store_id bigint not null,
  description text not null,
  brand text null,
  category text null,
  pack_size text null,
  unit text null default 'each',
  case_quantity integer null default 0,
  split_quantity integer null default 0,
  unit_price numeric(10, 2) null default 0,
  case_price numeric(10, 2) null default 0,
  vendor text null,
  location text null,
  manufacturer_code text null,
  par_level integer null default 0,
  reorder_point integer null default 0,
  is_sample_data boolean null default false,
  is_active boolean null default true,
  created_at timestamp with time zone null default now(),
  updated_at timestamp with time zone null default now()
);
\`\`\`

**ENHANCED FIELD EXTRACTION**: Look for these specific data patterns and map them to database fields:

**SUPC/Catalog Codes** (manufacturer_code field):
- Usually 4-6 digit numbers at start of line: "22124", "59711", "147963"
- Sometimes labeled as: "Item #", "Code", "SKU", "UPC"

**Brand vs Description**:
- Brand: Company/manufacturer name (Bombay, Jose Cuervo, Heinz, etc.)
- Description: Full product name including brand and details

**Pack/Size** (pack_size field):
- Look for: "4-1GAL", "12-49Z", "6-17FLZ", "96-2Z", "4-40Z"
- Patterns: number-number+unit, "case of X", "12 pack", etc.

**Vendor/Distributor** (vendor field):
- Often company names: "Great Lake", "GFS", "Sysco", "US Foods", "Atlantic Seafood", etc.
- May appear after product name or at end of line

**🚨 CRITICAL VENDOR ASSIGNMENT RULES:**
1. **Global/Document-Level Vendor**: If the document header, title, or filename indicates a single vendor (e.g., "Atlantic Seafood Order Sheet", "Sysco Invoice"), treat that as the global vendor for ALL items in the document. Assign it to every item unless an individual item explicitly names a different vendor.
2. **Per-Item Vendors**: If items in the document each explicitly list their own vendor next to them, use those per-item vendors.
3. **Pattern Break Detection**: If some items have a vendor explicitly listed and then subsequent items do NOT have a vendor listed:
   - If there was a global/document-level vendor established (from header/title), continue using that vendor for items without an explicit vendor.
   - If there was NO global vendor and the pattern shows a transition from "items with vendors" to "items without vendors", leave the vendor field as null for those items — do NOT carry forward the previous item's vendor.
4. **Summary**: A document-level vendor applies to all items. A per-item vendor only applies to that item. When vendors stop appearing on individual items and no document-level vendor exists, use null.

**Case vs Split Quantities**:
- Case Qty: Larger quantities, full cases (usually whole numbers)
- Split Qty: Partial cases, individual units from broken cases
- Unit: "EACH", "CASE", "BOX", "BOTTLE", "CAN", "JAR", "JUG", "PACKAGE"

**Pricing**:
- Unit Price: Price per individual unit (per bottle, per can, etc.)
- Case Price: Total price for full case quantity (Case Qty × Unit Price)

**Location Mapping**:
- Map storage areas to standard locations: Walk-in Cooler, Walk-in Freezer, Kitchen Prep Area, Kitchen Line, Dry Storage, Bar Area

**CRITICAL INSTRUCTIONS:**
1. **SCAN EVERY LINE**: Each chunk contains MULTIPLE inventory items - you MUST find ALL of them
2. **NO ITEM LEFT BEHIND**: Look for patterns like: Item Name + Quantity + Unit + Price
3. **MERGE SPLIT ITEMS**: Some items may be broken across multiple lines - merge them first
4. **EXTRACT EVERYTHING**: A typical chunk should yield 3-12 items - don't stop at the first one!
5. **ALCOHOL/WINE FOCUS**: Pay special attention to alcohol inventory (bottles, cases, etc.)
6. **LINE-BY-LINE ANALYSIS**: Process each line individually, then combine results

**CHUNK PROCESSING STRATEGY:**
- Line 1: Check for inventory item → Extract if found
- Line 2: Check for inventory item → Extract if found  
- Line 3: Check for inventory item → Extract if found
- Continue for ALL lines in the chunk
- Merge any split items that belong together

**File Content to Process:**
\`\`\`
${fileContent}
\`\`\`

**FINAL VERIFICATION CHECKLIST**: Before creating JSON output, ensure you have:
✅ **SCANNED EVERY SINGLE LINE** in the chunk for inventory items
✅ **FOUND ALL ITEMS** - Each chunk typically contains 3-12 inventory items  
✅ **MERGED SPLIT ITEMS** that were broken across multiple rows
✅ **EXTRACTED COMPLETE DATA** for each item (name, quantity, unit, price)
✅ **DOUBLE-CHECKED** that you didn't miss any items in the middle or end of the chunk

**EXPECT MULTIPLE ITEMS PER CHUNK** - Don't stop after finding the first item!

**CRITICAL**: Output ONLY a minimal JSON array. NO extra fields, NO reasoning, NO explanations, NO code blocks:

**EXAMPLE - SMART CATEGORY/LOCATION MATCHING:**
Input: "22124 Bombay Sapphire Great Lake 9 EACH $27.98 $251.82"
Store has existing categories: ["Alcohol", "Dairy", "Dry Goods", "Fresh Produce"]
Store has existing locations: ["Bar Area", "Walk-in Cooler", "Dry Storage", "Kitchen"]

Analysis: 
- SUPC: 22124 (catalog code)
- Brand: Bombay  
- Description: Bombay Sapphire
- Product type: Alcohol/Spirits
- ✅ Category: "Alcohol" (use existing, not "Beverage")
- ✅ Location: "Bar Area" (where alcohol is typically stored)
- Vendor: Great Lake
- Case Qty: 9, Unit: EACH, Unit Price: $27.98, Case Price: $251.82

Output: [{"store_id":${currentStore?.id || 'STORE_ID'},"description":"Bombay Sapphire","brand":"Bombay","category":"Alcohol","unit":"each","case_quantity":9,"unit_price":27.98,"case_price":251.82,"vendor":"Great Lake","location":"Bar Area","manufacturer_code":"22124"}]

**EXAMPLE 2 - DAIRY PRODUCT MATCHING:**
Input: "Butter unsalted 1lb blocks"
Store categories: ["Dairy", "Meat", "Produce"] → Use "Dairy" 
Store locations: ["Walk-in Cooler", "Freezer"] → Use "Walk-in Cooler" (dairy storage)
Output: category="Dairy", location="Walk-in Cooler"

**YOUR OUTPUT FORMAT - ENHANCED FIELDS:**
IF ITEMS FOUND:
[{"store_id":${currentStore?.id || 'STORE_ID'},"description":"Full product name","brand":"Brand name","category":"Category","pack_size":"Pack format","unit":"unit type","case_quantity":0,"split_quantity":0,"unit_price":0.00,"case_price":0.00,"vendor":"Vendor name","location":"Storage location","manufacturer_code":"Catalog code"}]

IF NO ITEMS FOUND (headers, formatting, etc.):
[]

**REQUIRED ENHANCED FIELDS**: 
- store_id (always ${currentStore?.id || 'STORE_ID'})
- description (full product name)
- brand (manufacturer/brand name if identifiable)
- category (Food/Beverage/Supplies/Cleaning/Paper Goods)
- pack_size (packaging format like "4-1GAL", "12-49Z")
- unit (each/case/box/bottle/can/jar/jug/package)
- case_quantity (number of units in full case)
- split_quantity (partial case quantity, 0 if full case)
- unit_price (price per individual unit)
- case_price (total price for case quantity)
- vendor (distributor/supplier name)
- location (storage area if mentioned)
- manufacturer_code (SUPC/catalog code if present)

**FIELD EXTRACTION PRIORITY**: Focus on finding SUPC codes, vendor names, case quantities, and both unit/case pricing from each line of inventory data.`;

    if (chunkInfo) {
      prompt += `\n\n**Processing Chunk:** ${chunkInfo}`;
    }

    return prompt;
  };

  // Process file with Claude API (with chunking for large files)
  const processWithClaude = async (fileContent) => {
    // Check if file content is too large for single processing
    const contentLength = fileContent.length;
    const maxSafeContentLength = 5000; // More conservative limit for dense tabular data
    
    if (contentLength > maxSafeContentLength) {
      console.log('📦 Large file detected, using chunking approach...');
      console.log('📊 File size:', contentLength, 'characters');
      
      return await processLargeFileInChunks(fileContent);
    } else {
      console.log('📄 Standard file size, processing normally...');
      return await processSingleChunk(fileContent);
    }
  };

  // Process large files by splitting into chunks
  const processLargeFileInChunks = async (fileContent) => {
    console.log('🔀 Splitting large file into manageable chunks...');
    
    // Split content into lines for better control
    const lines = fileContent.split('\n');
    const totalLines = lines.length;
    console.log('📋 Total lines to process:', totalLines);
    
    // Very conservative chunk size to stay well under 4096 completion token limit
    // With simplified JSON format (~150 tokens per item), target max 10-15 items per chunk
    // 12 lines should produce ~8-12 items = ~1200-1800 completion tokens (very safe margin)
    const chunkSize = 8; // Balanced - need enough context for Claude to find items
    const chunks = [];
    
    // Keep header information for context in each chunk
    const headerLines = lines.slice(0, 5); // First 5 lines usually contain headers/context
    
    // Split data lines into chunks
    for (let i = 5; i < totalLines; i += chunkSize) {
      const dataLines = lines.slice(i, i + chunkSize);
      const chunkContent = [...headerLines, '', '--- Data Section ---', ...dataLines].join('\n');
      chunks.push({
        content: chunkContent,
        startLine: i,
        endLine: Math.min(i + chunkSize - 1, totalLines - 1),
        size: dataLines.length
      });
    }
    
    console.log('📦 Created', chunks.length, 'chunks for processing');
    console.log('📊 Average chunk size:', Math.round(totalLines / chunks.length), 'lines');
    
    // Process each chunk
    const allResults = [];
    let chunkNumber = 1;
    
    // Initialize progress tracking
    setProgressInfo({
      currentChunk: 0,
      totalChunks: chunks.length,
      currentItem: 0,
      totalItems: 0,
      percentage: 0
    });
    
    for (const chunk of chunks) {
      console.log(`🔄 Processing chunk ${chunkNumber}/${chunks.length} (lines ${chunk.startLine}-${chunk.endLine}, ${chunk.size} data lines)...`);
      
      // Update progress
      setProgressInfo(prev => ({
        ...prev,
        currentChunk: chunkNumber,
        percentage: Math.round((chunkNumber / chunks.length) * 100)
      }));
      
      try {
        addDetailedStatus(`🔄 Processing chunk ${chunkNumber}/${chunks.length} (${chunk.size} lines)`, 'info');
        const chunkResults = await processSingleChunk(
          chunk.content, 
          `Chunk ${chunkNumber}/${chunks.length} (lines ${chunk.startLine}-${chunk.endLine})`
        );
        
        if (chunkResults && chunkResults.length > 0) {
          allResults.push(...chunkResults);
          console.log(`✅ Chunk ${chunkNumber} completed:`, chunkResults.length, 'items extracted');
          
          // Update progress with items found
          setProgressInfo(prev => ({
            ...prev,
            currentItem: prev.currentItem + chunkResults.length,
            totalItems: prev.totalItems + chunkResults.length
          }));
          
          // Create detailed item list for status display
          const itemDetails = chunkResults.map(item => 
            `${item.description || 'Unknown'} (${item.unit || 'each'})`
          );
          
          // Limit display to first 3 items to keep status readable
          const itemSummary = itemDetails.length > 3 
            ? `${itemDetails.slice(0, 3).join(', ')} +${itemDetails.length - 3} more`
            : itemDetails.join(', ');
          
          addDetailedStatus(`✅ Chunk ${chunkNumber}/${chunks.length}: ${itemSummary}`, 'success');
        } else {
          console.log(`⚠️ Chunk ${chunkNumber} returned no items`);
          addDetailedStatus(`⚠️ Chunk ${chunkNumber}/${chunks.length} returned no items`, 'error');
        }
      } catch (error) {
        console.error(`❌ Error processing chunk ${chunkNumber}:`, error.message);
        
        // Enhanced auto-retry logic for failed chunks
        const shouldRetryWithSmallerChunks = (
          error.message.includes('CHUNK_TOO_LARGE') ||
          error.message.includes('token') || 
          error.message.includes('JSON') ||
          error.message.includes('truncated') ||
          error.message.includes('Unterminated string') ||
          error.message.includes('position') ||
          error.message.includes('finish_reason: length')
        ) && chunk.size > 6;

        if (shouldRetryWithSmallerChunks) {
          console.log(`🔄 Chunk ${chunkNumber} failed (${error.message.split(':')[0]}), splitting into micro-chunks...`);
          addDetailedStatus(`🔄 Chunk ${chunkNumber} failed, trying micro-chunks...`, 'error');
          try {
            const smallerChunks = await splitChunkFurther(chunk.content, chunkNumber);
            allResults.push(...smallerChunks);
            console.log(`✅ Micro-chunk recovery successful for chunk ${chunkNumber}`);
            
            // Show what items were recovered from micro-chunks
            if (smallerChunks.length > 0) {
              const recoveredDetails = smallerChunks.map(item => 
                `${item.description || 'Unknown'} (${item.unit || 'each'})`
              );
              
              // Limit display for readability
              const recoveredSummary = recoveredDetails.length > 3 
                ? `${recoveredDetails.slice(0, 3).join(', ')} +${recoveredDetails.length - 3} more`
                : recoveredDetails.join(', ');
                
              addDetailedStatus(`✅ Micro-chunk ${chunkNumber} recovered: ${recoveredSummary}`, 'success');
            } else {
              addDetailedStatus(`✅ Micro-chunk recovery for chunk ${chunkNumber} complete (0 items)`, 'success');
            }
          } catch (splitError) {
            console.error(`❌ All retry attempts failed for chunk ${chunkNumber}:`, splitError.message);
            console.log(`⚠️ Skipping chunk ${chunkNumber} to continue with remaining chunks`);
            addDetailedStatus(`❌ Chunk ${chunkNumber} failed completely, skipping`, 'error');
          }
        } else {
          console.error(`❌ Chunk ${chunkNumber} failed with non-recoverable error:`, error.message);
          addDetailedStatus(`❌ Chunk ${chunkNumber} failed: ${error.message.split(':')[0]}`, 'error');
        }
      }
      
      chunkNumber++;
      
      // Small delay between chunks to avoid rate limiting
      if (chunkNumber <= chunks.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    console.log('🎉 All chunks processed! Total items extracted:', allResults.length);
    return allResults;
  };

  // Split a chunk further if it's still too large
  const splitChunkFurther = async (chunkContent, originalChunkNumber) => {
    console.log('🔀 Splitting chunk further into micro-chunks...');
    
    const lines = chunkContent.split('\n');
    const dataStartIndex = lines.findIndex(line => line.includes('--- Data Section ---')) + 1;
    const headerLines = lines.slice(0, dataStartIndex);
    const dataLines = lines.slice(dataStartIndex);
    
    const microChunkSize = 2; // Extremely small micro-chunks to absolutely guarantee completion under 4096 tokens
    const allMicroResults = [];
    
    for (let i = 0; i < dataLines.length; i += microChunkSize) {
      const microDataLines = dataLines.slice(i, i + microChunkSize);
      const microChunkContent = [...headerLines, ...microDataLines].join('\n');
      
      try {
        const microChunkResults = await processSingleChunk(
          microChunkContent, 
          `Sub-chunk of ${originalChunkNumber} (micro-chunk ${Math.floor(i / microChunkSize) + 1})`
        );
        
        if (microChunkResults && microChunkResults.length > 0) {
          allMicroResults.push(...microChunkResults);
        }
      } catch (error) {
        console.error(`❌ Micro-chunk failed:`, error.message);
      }
      
      // Delay between micro-chunks
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    return allMicroResults;
  };

  // Process a single chunk of data
  const processSingleChunk = async (fileContent, chunkInfo = '') => {
    const prompt = buildInventoryPrompt(fileContent, chunkInfo);

    console.log('🤖 Sending request to Claude API...', chunkInfo);
    
    // Enhanced input analysis
    const estimatedInputTokens = Math.ceil((prompt.length + fileContent.length) / 4); // Rough estimation
    const chunkLines = fileContent.split('\n').filter(line => line.trim());
    const dataLines = chunkLines.filter(line => !line.includes('---') && line.trim().length > 10);
    
    console.log('📊 Enhanced Request Analysis:');
    console.log('  - Model:', CONFIG.CLAUDE_MODEL);
    console.log('  - Max Completion Tokens:', CONFIG.MAX_TOKENS);
    console.log('  - Temperature:', CONFIG.TEMPERATURE);
    console.log('  - Total Input Length:', (prompt.length + fileContent.length), 'characters');
    console.log('  - Estimated Input Tokens:', estimatedInputTokens);
    console.log('  - Chunk Info:', chunkInfo);
    console.log('  - Data Lines in Chunk:', dataLines.length);
    console.log('  - Raw Lines in Chunk:', chunkLines.length);
    
    console.log('📋 Chunk Content Analysis:');
    console.log('  - First data line:', dataLines[0] || 'N/A');
    console.log('  - Last data line:', dataLines[dataLines.length - 1] || 'N/A');
    console.log('  - Sample lines (2-3):');
    dataLines.slice(1, 4).forEach((line, idx) => {
      console.log(`    ${idx + 2}. ${line.substring(0, 100)}${line.length > 100 ? '...' : ''}`);
    });
    
    console.log('📋 Prompt Preview (first 300 chars):', prompt.substring(0, 300) + '...');

    try {
      const requestPayload = {
        model: CONFIG.CLAUDE_MODEL,
        max_tokens: CONFIG.MAX_TOKENS,
        temperature: CONFIG.TEMPERATURE,
        system: 'You are a restaurant accountant expert at converting various inventory forms into structured database records. Always respond with valid JSON only.',
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ]
      };

      console.log('📤 Full API Request Payload:', JSON.stringify(requestPayload, null, 2));

      const response = await fetch(CONFIG.API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'anthropic-version': CONFIG.API_VERSION
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Claude API Error Response:', errorData);
        throw new Error(`Claude API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      
      const content = data.content?.[0]?.text;
      const finishReason = data.stop_reason;
      const completionTokens = data.usage?.output_tokens || 0;
      const promptTokens = data.usage?.input_tokens || 0;
      
      console.log('✅ Claude Raw API Response (Structure):', {
        content: data.content?.length || 0,
        usage: data.usage,
        model: data.model,
        contentLength: content?.length || 0
      });
      
      console.log('📊 Enhanced Response Analysis:');
      console.log('  - Completion tokens used:', completionTokens, '/ 4096 limit');
      console.log('  - Prompt tokens used:', promptTokens);
      console.log('  - Total tokens used:', data.usage?.total_tokens || 'unknown');
      console.log('  - Finish reason:', finishReason);
      console.log('  - Response length:', content?.length || 0, 'characters');
      console.log('  - Token efficiency:', completionTokens > 0 ? `${(content?.length / completionTokens).toFixed(2)} chars/token` : 'N/A');
      
      // Enhanced truncation analysis
      if (finishReason === 'length') {
        console.error('🚨 CRITICAL: Response was TRUNCATED due to token limit!');
        console.error('🔧 Required Actions:');
        console.error('  - Current completion tokens:', completionTokens, '(hit limit)');
        console.error('  - Current chunk has', dataLines.length, 'data lines');
        console.error('  - Recommend reducing chunk size to', Math.max(4, Math.floor(dataLines.length * 0.6)), 'lines');
        console.error('  - Or reduce AI verbosity in prompt');
        
        if (content) {
          console.error('🔍 Truncation Analysis:');
          console.error('  - Response ends with:', content.slice(-100));
          console.error('  - Last complete item likely ends around:', content.lastIndexOf('}'));
          console.error('  - Incomplete JSON detected at character:', content.length);
        }
      } else if (finishReason === 'stop') {
        console.log('✅ Response completed normally');
        console.log('🎯 Token usage efficiency:', `${((completionTokens / 4096) * 100).toFixed(1)}% of limit used`);
      } else {
        console.warn('⚠️ Unexpected finish reason:', finishReason);
      }

      if (!content) {
        throw new Error('No response content from Claude');
      }

      console.log('🤖 Claude response received');
      console.log('📋 Raw Response Content (full):', content);
      console.log('📏 Response Length:', content.length, 'characters');

              // Handle Claude returning {} instead of [] for empty results
      let processedContent = content;
      if (content.trim() === '{}') {
        console.log('🔄 Converting empty object {} to empty array []');
        processedContent = '[]';
      }

      // Advanced JSON validation and parsing with auto-retry capability
      const isValidJSON = (str) => {
        try {
          JSON.parse(str);
          return true;
        } catch {
          return false;
        }
      };

      const isCompleteJSON = (str) => {
        const trimmed = str.trim();
        return (trimmed.startsWith('[') && trimmed.endsWith(']')) || trimmed === '[]';
      };

      // Check if response looks truncated
      const isTruncated = finishReason === 'length';
      
      if (isTruncated) {
        console.warn('⚠️ RESPONSE TRUNCATED: Attempting smart recovery...');
      }

      try {
        // First, try direct parsing if response looks complete
        if (isCompleteJSON(processedContent)) {
          const parsedItems = JSON.parse(processedContent);
          const itemsArray = Array.isArray(parsedItems) ? parsedItems : [parsedItems];
          
          console.log('📊 Parsed Items Summary:');
          console.log('  - Items parsed:', itemsArray.length);
          console.log('  - Response status: Complete');
          console.log('  - Sample items (first 3):');
          itemsArray.slice(0, 3).forEach((item, index) => {
            console.log(`    ${index + 1}. ${item.description || 'No description'} - ${item.category || 'No category'} - Qty: ${item.case_quantity || 0} - Price: $${item.case_price || 0}`);
          });
          
          if (itemsArray.length > 3) {
            console.log(`    ... and ${itemsArray.length - 3} more items`);
          }
          
          return itemsArray;
        }
        
        // If not complete, attempt intelligent recovery
        throw new Error('Incomplete JSON detected, attempting recovery...');
        
      } catch (parseError) {
        console.error('❌ Direct JSON parsing failed:', parseError.message);
        console.log('🔍 Raw response debugging:');
        console.log('  - Length:', content.length, 'characters');
        console.log('  - Starts with:', content.substring(0, 100));
        console.log('  - Ends with:', content.substring(content.length - 100));
        console.log('  - Truncated:', isTruncated);
        
        // Enhanced debugging for JSON parsing errors
        console.log('🧪 Enhanced JSON Debugging & Truncation Analysis:');
        
        const errorPos = parseInt(parseError.message.match(/position (\d+)/)?.[1]) || 0;
        const errorLine = parseError.message.match(/line (\d+)/)?.[1] || 'unknown';
        const errorColumn = parseError.message.match(/column (\d+)/)?.[1] || 'unknown';
        const lastCompleteObject = content.lastIndexOf('}');
        const openBraces = (content.match(/\{/g) || []).length;
        const closeBraces = (content.match(/\}/g) || []).length;
        const bracketImbalance = openBraces - closeBraces;
        
        console.log('  🎯 Error Location:');
        console.log('    - Parse error position:', errorPos, '/', content.length);
        console.log('    - Error at line:', errorLine, 'column:', errorColumn);
        console.log('    - Character at error:', `"${content.charAt(errorPos)}" (code: ${content.charCodeAt(errorPos)})`);
        console.log('    - Error type:', parseError.message.split(':')[0]);
        
        console.log('  📊 JSON Structure Analysis:');
        console.log('    - Starts with [:', content.trim().startsWith('['));
        console.log('    - Ends with ]:', content.trim().endsWith(']'));
        console.log('    - Open braces { count:', openBraces);
        console.log('    - Close braces } count:', closeBraces);
        console.log('    - Bracket imbalance:', bracketImbalance, bracketImbalance > 0 ? '(missing closing braces)' : '(balanced)');
        console.log('    - Last complete object ends at:', lastCompleteObject);
        console.log('    - Distance from error to last object:', errorPos - lastCompleteObject, 'chars');
        
        console.log('  🔍 Context Analysis:');
        const startPos = Math.max(0, errorPos - 75);
        const endPos = Math.min(content.length, errorPos + 75);
        const contextBefore = content.substring(startPos, errorPos);
        const contextAfter = content.substring(errorPos, endPos);
        console.log('    - Context before error (75 chars):', `"${contextBefore.replace(/\n/g, '\\n')}"`);
        console.log('    - Context after error (75 chars):', `"${contextAfter.replace(/\n/g, '\\n')}"`);
        
        console.log('  🧮 Token Usage Impact:');
        console.log('    - Used', completionTokens, '/ 4096 completion tokens');
        console.log('    - Truncation confidence:', finishReason === 'length' ? 'HIGH (hit token limit)' : 'LOW');
        console.log('    - Estimated items in response:', Math.floor(openBraces / 2), '(based on brace count)');
        console.log('    - Chunk contained', dataLines.length, 'input lines');
        
        if (finishReason === 'length' && bracketImbalance > 0) {
          console.error('  🚨 DIAGNOSIS: Response was CUT OFF mid-item due to 4096 token limit');
          console.error('    - Suggested fix: Reduce chunk size from', dataLines.length, 'to', Math.max(3, Math.floor(dataLines.length * 0.5)), 'lines');
        }
        
        // Advanced recovery techniques
        console.log('🔄 Attempting intelligent JSON recovery...');
        
        try {
          let recoveredItems = [];
          
          // Method 1: Try to find and repair the JSON structure
          let repairedJson = processedContent;
          
          // Ensure it starts with [
          if (!repairedJson.trim().startsWith('[')) {
            const openBracket = repairedJson.indexOf('[');
            if (openBracket !== -1) {
              repairedJson = repairedJson.substring(openBracket);
            }
          }
          
          // Method 2: Find complete objects and build array
          const objectPattern = /\{[^{}]*"description"[^{}]*\}/g;
          const completeObjects = repairedJson.match(objectPattern);
          
          if (completeObjects && completeObjects.length > 0) {
            console.log(`🔧 Found ${completeObjects.length} complete objects in truncated response`);
            
            // Try to parse each object individually
            const validObjects = [];
            completeObjects.forEach((objStr, index) => {
              try {
                const obj = JSON.parse(objStr);
                if (obj.description) {
                  validObjects.push(obj);
                }
              } catch (e) {
                console.log(`⚠️ Object ${index + 1} failed individual parsing:`, e.message);
              }
            });
            
            if (validObjects.length > 0) {
              console.log(`✅ Successfully recovered ${validObjects.length} items from truncated response`);
              return validObjects;
            }
          }
          
          // Method 3: Line-by-line reconstruction
          const lines = processedContent.split('\n');
          let jsonBuilder = '';
          let bracketCount = 0;
          let inArray = false;
          
          for (let line of lines) {
            jsonBuilder += line + '\n';
            
            // Track brackets
            for (let char of line) {
              if (char === '[') {
                inArray = true;
                bracketCount++;
              } else if (char === ']') {
                bracketCount--;
              }
            }
            
            // Try to parse when we have what looks like a complete structure
            if (inArray && line.includes('}')) {
              let testJson = jsonBuilder.trim();
              
              // Fix common truncation issues
              if (!testJson.endsWith(']')) {
                // Remove trailing comma and add closing bracket
                testJson = testJson.replace(/,\s*$/, '') + ']';
              }
              
              if (isValidJSON(testJson)) {
                const parsed = JSON.parse(testJson);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  recoveredItems = parsed;
                }
              }
            }
          }
          
          if (recoveredItems.length > 0) {
            console.log(`✅ Line-by-line recovery successful: ${recoveredItems.length} items`);
            return recoveredItems;
          }
          
        } catch (recoveryError) {
          console.error('❌ All recovery methods failed:', recoveryError.message);
        }
        
        // If this chunk was truncated and we're in chunking mode, signal for smaller retry
        if (isTruncated && chunkInfo) {
          console.log('💡 Suggesting smaller chunk size for retry...');
          throw new Error('CHUNK_TOO_LARGE: Response truncated, need smaller chunks');
        }
        
        throw new Error('Complete JSON parsing failure: ' + parseError.message);
      }

    } catch (error) {
              console.error('❌ Claude API error:', error);
      throw error;
    }
  };

  const extractJSONArray = (rawText) => {
    if (!rawText) {
      throw new Error('No response content from AI');
    }

    const stripped = rawText
      .replace(/```json/gi, '')
      .replace(/```/g, '')
      .trim();

    try {
      const parsed = JSON.parse(stripped);
      if (Array.isArray(parsed)) return parsed;
      if (Array.isArray(parsed?.items)) return parsed.items;
      if (parsed && typeof parsed === 'object') return [parsed];
    } catch (error) {
      // Continue to bracket extraction fallback below.
    }

    const arrayMatch = stripped.match(/\[[\s\S]*\]/);
    if (arrayMatch) {
      const parsed = JSON.parse(arrayMatch[0]);
      if (Array.isArray(parsed)) return parsed;
    }

    throw new Error('AI response was not valid JSON array output');
  };

  const processCameraImageWithClaude = async (file) => {
    const image = await readImageFile(file);
    const locations = existingStoreData.locations?.length
      ? existingStoreData.locations.join(', ')
      : 'Walk-in Cooler, Walk-in Freezer, Kitchen Prep Area, Dry Storage, Bar Area';
    const categories = existingStoreData.categories?.length
      ? existingStoreData.categories.join(', ')
      : 'Food, Beverage, Supplies, Cleaning, Paper Goods';

    const prompt = `Extract restaurant inventory items from this photographed list.

Output ONLY a JSON array of objects. No markdown or commentary.
Use this schema:
[{"store_id":${currentStore?.id || 'STORE_ID'},"description":"...","brand":"...","category":"...","pack_size":"...","unit":"each","case_quantity":0,"split_quantity":0,"unit_price":0.00,"case_price":0.00,"vendor":"...","location":"...","manufacturer_code":"..."}]

Guidelines:
- Parse every visible line item in the image.
- Use existing locations when possible: ${locations}
- Use existing categories when possible: ${categories}
- If a field is unknown, use null (or 0 for numeric quantity/price fields).
- description must be present for each item.

Vendor assignment rules:
- If the image shows a document header, title, or letterhead indicating a single vendor/supplier (e.g., "Atlantic Seafood", "Sysco Order"), assign that vendor to ALL items in the list.
- If individual items each list their own vendor, use those per-item vendors.
- If some items have vendors listed and then subsequent items do not, AND there is a document-level vendor visible, continue using that vendor for all items.
- If some items have vendors listed and then subsequent items do not, AND there is NO document-level vendor, leave vendor as null for items that do not explicitly show one — do NOT carry forward a previous item's vendor.`;

    const requestPayload = {
      model: CONFIG.CLAUDE_MODEL,
      max_tokens: CONFIG.MAX_TOKENS,
      temperature: CONFIG.TEMPERATURE,
      system: 'You are a restaurant inventory extraction assistant. Return valid JSON arrays only.',
      messages: [
        {
          role: 'user',
          content: [
            { type: 'text', text: prompt },
            {
              type: 'image',
              source: {
                type: 'base64',
                media_type: image.mediaType,
                data: image.data
              }
            }
          ]
        }
      ]
    };

    const response = await fetch(CONFIG.API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-version': CONFIG.API_VERSION
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Claude API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();
    const content = data.content?.[0]?.text;
    const extractedItems = extractJSONArray(content);
    if (!Array.isArray(extractedItems) || extractedItems.length === 0) {
      throw new Error('No valid inventory rows were detected in the image');
    }

    return extractedItems;
  };

  const handleManualUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file first');
      return;
    }
    await processFilesWithAI(selectedFiles);
  };

  // ─── Edge Function integration (same API as menu starter) ───
  const getEdgeFunctionHeaders = async () => {
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) {
        return {
          Authorization: `Bearer ${data.session.access_token}`,
          apikey: window.SUPABASE_KEY,
          'Content-Type': 'application/json',
        };
      }
    } catch (_) {}
    // Fallback: try localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(key));
        if (parsed?.access_token) {
          return {
            Authorization: `Bearer ${parsed.access_token}`,
            apikey: window.SUPABASE_KEY,
            'Content-Type': 'application/json',
          };
        }
      } catch (_) {}
    }
    throw new Error('Not authenticated. Please sign in and try again.');
  };

  const processViaEdgeFunction = async (uploadedSource) => {
    const ENDPOINT = `${window.SUPABASE_URL}/functions/v1/menu-starter`;
    const headers = await getEdgeFunctionHeaders();

    // Start workflow
    const startResp = await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'start_discovery',
        skip_discovery: true,
        source_type: 'document_import',
        uploaded_menu_sources: [uploadedSource],
        store_id: currentStore?.id || null,
        source: 'import',
      }),
    });
    const startData = await startResp.json().catch(() => ({}));
    if (!startResp.ok || !startData.ok) {
      throw new Error(
        formatErrorMessage(startData.error, `Edge Function error: ${startResp.status}`)
      );
    }

    const workflowId = startData.workflow_id;
    console.log('🤖 Workflow started:', workflowId);

    // Poll for completion
    const maxPollTime = 25 * 60 * 1000;
    const enrichmentStallLimit = 7 * 60 * 1000;
    const pollInterval = 3000;
    const startTime = Date.now();
    let lastWorkflow = null;
    let lastEnrichmentSignature = '';
    let lastEnrichmentProgressAt = Date.now();

    while (Date.now() - startTime < maxPollTime) {
      await new Promise(r => setTimeout(r, pollInterval));

      const pollResp = await fetch(ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'get_workflow', workflow_id: workflowId }),
      });
      const pollData = await pollResp.json().catch(() => ({}));
      if (!pollResp.ok || !pollData.workflow) continue;

      const wf = pollData.workflow;
      lastWorkflow = wf;
      const elapsed = Math.round((Date.now() - startTime) / 1000);
      const progress = wf.metadata?.document_import_progress || null;
      if (progress && progress.current_source) {
        const completed = progress.completed_sources || 0;
        const total = progress.total_sources || 0;
        const batch = progress.current_batch || 0;
        const totalBatches = progress.total_batches || 0;
        const pass = progress.current_pass || 0;
        const extracted = progress.extracted_items_so_far || 0;
        setProcessingStatus(
          `${wf.status} • ${progress.current_source} • sources ${completed}/${total} • batch ${batch}/${totalBatches} • pass ${pass} • ${extracted} items • ${elapsed}s`,
        );

        // Update modal with live progress
        const phases = ['Extracting items…', 'Normalizing units…', 'Categorizing products…', 'Grouping by location…'];
        setImportStatusText(importProgressMessage(progress) || phases[pass % phases.length] || 'Processing…');
        setImportItemsProcessed(extracted);
        if (progress.estimated_total_items) {
          setImportTotalEstimated(progress.estimated_total_items);
          setImportProgressPct(Math.round((extracted / progress.estimated_total_items) * 100));
        }

        // Push recently extracted items into feed
        const recentItems = wf.metadata?.recently_extracted_items || [];
        if (recentItems.length > 0) {
          recentItems.forEach(itemName => {
            addFeedItem(itemName, pass <= 1 ? 'parsing' : pass <= 2 ? 'normalizing' : 'categorizing');
          });
        }
      } else {
        setProcessingStatus(`AI is processing... (${wf.status}) ${elapsed}s`);
        setImportStatusText(elapsed < 10 ? 'Analyzing document…' : 'Extracting items…');
      }

      if (wf.status === 'completed') {
        // Rows become durable before the fresh-clock related-product pass is
        // done. Keep polling so consumers receive the complete outcomes.
        const enrichmentJob = wf.metadata?.import_enrichment;
        if (enrichmentJob && (enrichmentJob.status === 'pending' || enrichmentJob.status === 'running')) {
          const enrichmentSignature = [
            enrichmentJob.status,
            enrichmentJob.phase,
            enrichmentJob.phase_at,
            enrichmentJob.relationship_cursor,
          ].join('|');
          if (enrichmentSignature !== lastEnrichmentSignature) {
            lastEnrichmentSignature = enrichmentSignature;
            lastEnrichmentProgressAt = Date.now();
          }
          if (Date.now() - lastEnrichmentProgressAt > enrichmentStallLimit) break;
          continue;
        }
        return {
          items: wf.generated_items_preview || [],
          // Source-batch accounting: how many VENDOR PRODUCTS this call
          // imported, and how many came from each vendor. The preview holds one
          // entry per canonical item, so on a multi-vendor list it is smaller
          // than the batch and names only the vendor mirrored onto each item.
          vendorProductCount: Number(wf.metadata?.imported_vendor_products || 0) || 0,
          vendorBreakdown: Array.isArray(wf.metadata?.imported_vendor_breakdown)
            ? wf.metadata.imported_vendor_breakdown
              .filter((entry) => entry && entry.name)
              .map((entry) => ({ name: String(entry.name), count: Number(entry.count) || 0 }))
            : [],
          longListReview: wf.metadata?.long_list_review || null,
          iosFreeLimit: wf.metadata?.ios_free_limit_reached === true,
        };
      }
      if (wf.status === 'failed') {
        const publicMsg = formatErrorMessage(wf.last_error_public, 'AI processing failed.');
        const internalMsg = formatErrorMessage(wf.last_error_internal, '');
        const genericFailure = /failed to process uploaded files/i.test(publicMsg);
        const message = (genericFailure && internalMsg)
          ? `${publicMsg} ${internalMsg}`
          : publicMsg;
        throw new Error(message);
      }
    }
    if (lastWorkflow?.status === 'completed') {
      const metadata = lastWorkflow.metadata || {};
      return {
        items: lastWorkflow.generated_items_preview || [],
        vendorProductCount: Number(metadata.imported_vendor_products || 0) || 0,
        vendorBreakdown: Array.isArray(metadata.imported_vendor_breakdown)
          ? metadata.imported_vendor_breakdown
          : [],
        longListReview: metadata.long_list_review || null,
        iosFreeLimit: metadata.ios_free_limit_reached === true,
        serverFinishing: true,
      };
    }
    throw new Error('AI processing timed out before the import was saved. Try again or use Save progress in setup flow.');
  };

  // Extract text from PDF page by page using PDF.js
  const extractPdfPages = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          if (typeof pdfjsLib === 'undefined') {
            // Fallback: send as base64 if PDF.js not available
            const bytes = new Uint8Array(e.target.result);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            resolve({ type: 'base64', data: btoa(binary), pages: [] });
            return;
          }

          pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
          const pdfData = new Uint8Array(e.target.result);
          const pdf = await pdfjsLib.getDocument(pdfData).promise;
          const totalPages = pdf.numPages;
          console.log(`📄 PDF has ${totalPages} pages`);

          const pages = [];
          for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            if (pageText.trim().length > 10) {
              pages.push({ pageNum, text: pageText });
            }
          }

          resolve({ type: 'pages', pages, totalPages });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Error reading PDF'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Extract text from Excel in row chunks
  const extractExcelChunks = (file, chunkSize = 50) => {
    return new Promise((resolve, reject) => {
      if (typeof XLSX === 'undefined') {
        reject(new Error('Excel support requires SheetJS library'));
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = selectInventorySheetName(workbook);
          if (!sheetName) {
            reject(new Error('Could not find a usable worksheet in this Excel file.'));
            return;
          }
          const sheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

          // Find header row (first row with 3+ non-empty cells)
          let headerIdx = 0;
          for (let i = 0; i < Math.min(rows.length, 10); i++) {
            const nonEmpty = rows[i].filter(c => String(c).trim()).length;
            if (nonEmpty >= 3) { headerIdx = i; break; }
          }
          const header = rows[headerIdx].map(c => String(c).trim());
          const dataRows = rows.slice(headerIdx + 1).filter(r => r.some(c => String(c).trim()));

          const sourceFingerprint = `${file.name}:${file.size}:${file.lastModified || 0}`;
          const chunks = [];
          for (let i = 0; i < dataRows.length; i += chunkSize) {
            const chunk = dataRows.slice(i, i + chunkSize);
            const csvText = [header.join(','), ...chunk.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
            chunks.push({ chunkIndex: Math.floor(i / chunkSize) + 1, text: csvText, rowCount: chunk.length, sourceFingerprint });
          }

          console.log(`📊 Excel sheets: [${workbook.SheetNames.join(', ')}] → selected "${sheetName}"`);
          console.log(`📊 Excel: ${dataRows.length} rows → ${chunks.length} chunks of ~${chunkSize}`);
          resolve({ totalRows: dataRows.length, chunks, sourceFingerprint });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Error reading Excel'));
      reader.readAsArrayBuffer(file);
    });
  };

  const collectItemIds = (collector, items) => {
    if (!Array.isArray(collector) || !Array.isArray(items)) return;
    items.forEach((item) => {
      const id = item?.id;
      if (id != null && !Number.isNaN(Number(id))) collector.push(Number(id));
    });
  };

  const collectItemVendors = (vendorCollector, items) => {
    if (!vendorCollector || !Array.isArray(items)) return;
    items.forEach((item) => {
      const vendor = (item?.vendor || '').trim();
      if (vendor) {
        vendorCollector[vendor] = (vendorCollector[vendor] || 0) + 1;
      }
    });
  };

  // ── Source-batch accounting for one edge-function call ─────────────────────
  // The summary reports every VENDOR PRODUCT the user handed us, not the
  // canonical catalog identities they landed on. Those diverge whenever one
  // product is quoted by several vendors: a 76-row two-vendor price list
  // becomes 38 inventory items, so counting items reported "38 items from 3
  // vendors" while 38 US Foods / Worldwide Produce rows had in fact been
  // imported as purchase options. menu-starter reports the batch figures in
  // workflow metadata.
  //
  // The batch figures describe the WHOLE call, so they are only usable when the
  // per-document cap did not truncate that call's items; a truncated call keeps
  // the item-derived numbers so the cap accounting stays exact.
  const applyBatchAccounting = (result, items, accepted, vendorCollector) => {
    const truncated = Array.isArray(items) && accepted < items.length;
    const breakdown = Array.isArray(result?.vendorBreakdown) ? result.vendorBreakdown : [];
    const reportedCount = Number(result?.vendorProductCount) || 0;
    if (truncated || breakdown.length === 0 || reportedCount <= 0) {
      collectItemVendors(vendorCollector, Array.isArray(items) ? items.slice(0, accepted) : []);
      return accepted;
    }
    if (vendorCollector) {
      breakdown.forEach((entry) => {
        const name = String(entry?.name || '').trim();
        const count = Number(entry?.count) || 0;
        if (name && count > 0) vendorCollector[name] = (vendorCollector[name] || 0) + count;
      });
    }
    return reportedCount;
  };

  const processSingleFileWithAI = async (file, fileIndex = 1, totalFiles = 1, idCollector = null, vendorCollector = null) => {
    setAiProcessing(true);
    console.log(`🤖 Starting AI import ${fileIndex}/${totalFiles}:`, file?.name);
    const cameraImage = isImageFile(file);
    const isPdf = file.name?.toLowerCase().endsWith('.pdf');
    const isExcel = file.name?.toLowerCase().match(/\.xlsx?$/);

    // ─── Image files: send as base64 (unchanged) ───
    if (cameraImage) {
      setProcessingStatus(`📷 Reading image ${fileIndex}/${totalFiles}...`);
      addDetailedStatus(`📷 Processing image file ${file.name}`, 'info');
      const imageData = await readImageFile(file);
      const uploadedSource = {
        file_name: file.name || 'photo.jpg',
        content_type: imageData.mediaType || getImageMimeFromName(file.name),
        pdf_base64: imageData.data,
      };
      setProcessingStatus(`AI analyzing ${file.name}...`);
      const result = await processViaEdgeFunction(uploadedSource);
      const items = result?.items;
      const count = Array.isArray(items) ? Math.min(items.length, MAX_ITEMS_PER_DOCUMENT) : 0;
      collectItemIds(idCollector, items);
      const imported = applyBatchAccounting(result, items, count, vendorCollector);
      if (result?.longListReview) {
        setDeferredReviewNotice(result.longListReview);
      }
      addDetailedStatus(`${imported > 0 ? '✅' : '⚠️'} ${file.name}: ${imported} items`, imported > 0 ? 'success' : 'error');
      return imported;
    }

    // ─── PDF files: extract text per page, process page by page ───
    if (isPdf) {
      setProcessingStatus(`📄 Reading PDF pages...`);
      addDetailedStatus(`📄 Extracting text from ${file.name}`, 'info');
      setImportStatusText('Reading PDF pages…');

      const pdfResult = await extractPdfPages(file);

      // Helper to send full PDF as base64
      const sendFullPdfBase64 = async () => {
        setImportStatusText('Processing full PDF…');
        addDetailedStatus(`📄 Sending full PDF to AI`, 'info');
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const bytes = new Uint8Array(e.target.result);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            resolve(btoa(binary));
          };
          reader.onerror = () => reject(new Error('Error reading PDF'));
          reader.readAsArrayBuffer(file);
        });
        const result = await processViaEdgeFunction({
          file_name: file.name || 'document.pdf',
          content_type: 'application/pdf',
          pdf_base64: base64,
        });
        const pdfCap = Array.isArray(result?.items) ? Math.min(result.items.length, MAX_ITEMS_PER_DOCUMENT) : 0;
        collectItemIds(idCollector, result?.items);
        const pdfImported = applyBatchAccounting(result, result?.items, pdfCap, vendorCollector);
        if (result?.longListReview) {
          setDeferredReviewNotice(result.longListReview);
        }
        return pdfImported;
      };

      if (pdfResult.type === 'base64') {
        // PDF.js not available, send as base64
        addDetailedStatus(`⚠️ PDF.js not available, sending full PDF to AI`, 'info');
        return await sendFullPdfBase64();
      }

      // Check text quality — scanned/image PDFs have very little extractable text
      const { pages, totalPages } = pdfResult;
      const avgTextLen = pages.length > 0 ? pages.reduce((s, p) => s + p.text.length, 0) / pages.length : 0;
      console.log(`📄 Avg text per page: ${Math.round(avgTextLen)} chars`);

      if (avgTextLen <= 50) {
        addDetailedStatus(`⚠️ Scanned PDF detected (${Math.round(avgTextLen)} chars/page), sending full PDF`, 'info');
        addFeedItem('Scanned PDF detected — sending full document', 'parsing');
        return await sendFullPdfBase64();
      }

      // Good text — process page by page. totalImported stays an ITEM count so
      // the per-document cap keeps its exact meaning; batchRows accumulates the
      // vendor products those items represent and is what the summary reports.
      let totalImported = 0;
      let batchRows = 0;
      setImportTotalEstimated(pages.length * 15);
      addDetailedStatus(`📄 ${totalPages} pages, ${pages.length} with content (${Math.round(avgTextLen)} chars/page)`, 'info');
      let reachedDocumentCap = false;

      for (let i = 0; i < pages.length; i++) {
        if (totalImported >= MAX_ITEMS_PER_DOCUMENT) {
          reachedDocumentCap = true;
          break;
        }
        const page = pages[i];
        setImportStatusText(`Processing page ${page.pageNum} of ${totalPages}…`);
        setImportProgressPct(Math.round(((i + 1) / pages.length) * 100));
        addFeedItem(`Page ${page.pageNum} of ${totalPages}`, 'parsing');
        setProcessingStatus(`Page ${page.pageNum}/${totalPages} of ${file.name}...`);

        try {
          const result = await processViaEdgeFunction({
            file_name: `${file.name} (page ${page.pageNum})`,
            content_type: 'text/plain',
            text: page.text,
          });
          const count = Array.isArray(result?.items) ? result.items.length : 0;
          const remaining = Math.max(0, MAX_ITEMS_PER_DOCUMENT - totalImported);
          const accepted = Math.min(count, remaining);
          collectItemIds(idCollector, result?.items);
          batchRows += applyBatchAccounting(result, result?.items, accepted, vendorCollector);
          if (result?.longListReview) {
            setDeferredReviewNotice(result.longListReview);
          }
          totalImported += accepted;
          setImportItemsProcessed(totalImported);
          if (accepted > 0) {
            addFeedItem(`Page ${page.pageNum}: ${accepted} items found`, 'done');
            addDetailedStatus(`✅ Page ${page.pageNum}: ${accepted} items`, 'success');
          } else {
            addFeedItem(`Page ${page.pageNum}: no items`, 'done');
          }
          if (count > accepted) {
            reachedDocumentCap = true;
            addDetailedStatus(`⚠️ Reached ${MAX_ITEMS_PER_DOCUMENT}-item cap for ${file.name}. Remaining pages deferred for support review.`, 'info');
            break;
          }
        } catch (err) {
          console.error(`❌ Error processing page ${page.pageNum}:`, err);
          addDetailedStatus(`❌ Page ${page.pageNum} error: ${err.message}`, 'error');
        }
      }

      if (reachedDocumentCap && !deferredReviewNotice) {
        setDeferredReviewNotice({
          required: true,
          item_limit: MAX_ITEMS_PER_DOCUMENT,
          deferred_sources: [{ source_name: file.name, source_type: 'pdf' }],
          support_notified: true,
          message: `This file appears to contain more than ${MAX_ITEMS_PER_DOCUMENT} items. Imported items up to the cap were kept and support review has been queued.`,
        });
      }

      // If page-by-page got nothing, fall back to full PDF base64
      if (totalImported === 0) {
        console.log('⚠️ Page-by-page returned 0 items, falling back to full PDF');
        addFeedItem('Retrying with full PDF…', 'parsing');
        return await sendFullPdfBase64();
      }

      addDetailedStatus(`${totalImported > 0 ? '✅' : '⚠️'} ${file.name}: ${totalImported} total items from ${pages.length} pages`, totalImported > 0 ? 'success' : 'error');
      return batchRows || totalImported;
    }

    // ─── Excel files: parse and send in row chunks ───
    if (isExcel) {
      setProcessingStatus(`📊 Reading Excel file...`);
      addDetailedStatus(`📊 Parsing ${file.name}`, 'info');
      setImportStatusText('Parsing spreadsheet…');

      const excelResult = await extractExcelChunks(file, 50);
      const { totalRows, chunks } = excelResult;
      setImportTotalEstimated(totalRows);
      addDetailedStatus(`📊 ${totalRows} rows → ${chunks.length} chunks`, 'info');

      // totalImported stays an ITEM count so the per-document cap keeps its
      // exact meaning; batchRows accumulates the vendor products those items
      // represent and is what the summary reports.
      let totalImported = 0;
      let batchRows = 0;
      let reachedDocumentCap = false;
      for (let i = 0; i < chunks.length; i++) {
        if (totalImported >= MAX_ITEMS_PER_DOCUMENT) {
          reachedDocumentCap = true;
          break;
        }
        const chunk = chunks[i];
        setImportStatusText(`Processing rows ${i * 50 + 1}–${i * 50 + chunk.rowCount} of ${totalRows}…`);
        setImportProgressPct(Math.round(((i + 1) / chunks.length) * 100));
        addFeedItem(`Chunk ${chunk.chunkIndex}: rows ${i * 50 + 1}–${i * 50 + chunk.rowCount}`, 'parsing');
        setProcessingStatus(`Chunk ${chunk.chunkIndex}/${chunks.length} of ${file.name}...`);

        const uploadedSource = {
          file_name: `${file.name} (chunk ${chunk.chunkIndex})`,
          content_type: 'text/csv',
          text: chunk.text,
          source_fingerprint: chunk.sourceFingerprint || `${file.name}:${file.size}:${file.lastModified || 0}`,
        };

        try {
          const result = await processViaEdgeFunction(uploadedSource);
          const count = Array.isArray(result?.items) ? result.items.length : 0;
          const remaining = Math.max(0, MAX_ITEMS_PER_DOCUMENT - totalImported);
          const accepted = Math.min(count, remaining);
          collectItemIds(idCollector, result?.items);
          batchRows += applyBatchAccounting(result, result?.items, accepted, vendorCollector);
          if (result?.longListReview) {
            setDeferredReviewNotice(result.longListReview);
          }
          totalImported += accepted;
          setImportItemsProcessed(totalImported);
          addFeedItem(`Chunk ${chunk.chunkIndex}: ${accepted} items`, 'done');
          if (count > accepted) {
            reachedDocumentCap = true;
            addDetailedStatus(`⚠️ Reached ${MAX_ITEMS_PER_DOCUMENT}-item cap for ${file.name}. Remaining rows deferred for support review.`, 'info');
            break;
          }
        } catch (err) {
          console.error(`❌ Error processing chunk ${chunk.chunkIndex}:`, err);
          addDetailedStatus(`❌ Chunk ${chunk.chunkIndex} error: ${err.message}`, 'error');
        }
      }

      if (reachedDocumentCap && !deferredReviewNotice) {
        setDeferredReviewNotice({
          required: true,
          item_limit: MAX_ITEMS_PER_DOCUMENT,
          deferred_sources: [{ source_name: file.name, source_type: 'spreadsheet' }],
          support_notified: true,
          message: `This file appears to contain more than ${MAX_ITEMS_PER_DOCUMENT} items. Imported items up to the cap were kept and support review has been queued.`,
        });
      }

      addDetailedStatus(`${totalImported > 0 ? '✅' : '⚠️'} ${file.name}: ${totalImported} total items`, totalImported > 0 ? 'success' : 'error');
      return batchRows || totalImported;
    }

    // ─── CSV / Text files: send as-is (unchanged) ───
    setProcessingStatus(`📄 Reading file ${fileIndex}/${totalFiles}...`);
    addDetailedStatus(`📄 Processing text file ${file.name}`, 'info');
    const textContent = await readFileContent(file);
    const uploadedSource = {
      file_name: file.name || 'document.txt',
      content_type: 'text/plain',
      text: textContent,
    };

    setProcessingStatus(`AI analyzing ${file.name} (${fileIndex}/${totalFiles})...`);
    addDetailedStatus(`🚀 Sent ${file.name} to AI`, 'info');
    const result = await processViaEdgeFunction(uploadedSource);
    const importedCount = Array.isArray(result?.items) ? Math.min(result.items.length, MAX_ITEMS_PER_DOCUMENT) : 0;
    collectItemIds(idCollector, result?.items);
    const importedRows = applyBatchAccounting(result, result?.items, importedCount, vendorCollector);
    if (result?.iosFreeLimit) {
      if (window.PaywallModal?.showIosItemLimit) window.PaywallModal.showIosItemLimit();
      else alert('Item limit reached. Your free account includes up to 100 inventory items. Remove an existing item to add a new one.');
    }
    if (result?.longListReview) {
      setDeferredReviewNotice(result.longListReview);
    }
    if (Array.isArray(result?.items) && result.items.length > MAX_ITEMS_PER_DOCUMENT && !deferredReviewNotice) {
      setDeferredReviewNotice({
        required: true,
        item_limit: MAX_ITEMS_PER_DOCUMENT,
        deferred_sources: [{ source_name: file.name, source_type: 'text' }],
        support_notified: true,
        message: `This file appears to contain more than ${MAX_ITEMS_PER_DOCUMENT} items. Imported items up to the cap were kept and support review has been queued.`,
      });
    }
    addDetailedStatus(`${importedRows > 0 ? '✅' : '⚠️'} ${file.name}: ${importedRows} items`, importedRows > 0 ? 'success' : 'error');
    return importedRows;
  };

  const fetchRecentImportedItems = async (startedAtIso, expectedCount, ids = []) => {
    const storeId = currentStore.id || currentStore.store_id;
    const uniqueIds = Array.from(new Set((ids || []).filter((id) => id != null && !Number.isNaN(Number(id))).map((id) => Number(id))));

    if (uniqueIds.length > 0) {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('store_id', storeId)
        .in('id', uniqueIds)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      if (data && data.length) return data;
    }

    const { data, error } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('store_id', storeId)
      .gte('updated_at', startedAtIso)
      .order('updated_at', { ascending: false })
      .limit(Math.max((expectedCount || 0) + 20, 150));
    if (error) throw error;
    return data || [];
  };

  // ─── AI Pre-Classification ───
  const classifyDocument = async (file) => {
    try {
      const headers = await getEdgeFunctionHeaders();
      if (!headers) return null;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('action', 'classify');
      formData.append('store_id', String(currentStore?.id || ''));

      // Use the edge function for classification
      const resp = await fetch(`${CONFIG.EDGE_FUNCTION_URL || `https://hpmbxwbuoamijdyhpele.supabase.co/functions/v1/menu-starter`}`, {
        method: 'POST',
        headers: { ...headers },
        body: JSON.stringify({
          action: 'classify_document',
          store_id: currentStore?.id,
          file_name: file.name,
          file_type: file.type,
        }),
      });

      if (!resp.ok) return null;
      const result = await resp.json();
      return result; // { type, large, cuisine }
    } catch (e) {
      console.warn('Classification failed, proceeding without:', e);
      return null;
    }
  };

  // Generate humor message based on cuisine
  const getHumorMessage = (cuisine) => {
    const messages = {
      liquor: "As an AI, I'm not much of a drinker—this may take a moment.",
      italian: "My Italian is a bit rusty—working through this carefully.",
      french: "French cuisine can be… detailed. Give me a moment.",
      food_general: "Reviewing your items—this shouldn't take long.",
      mixed: "Quite the variety here—processing now.",
    };
    return messages[cuisine] || messages.food_general;
  };

  // Add item to the modal's live feed
  const addFeedItem = useCallback((name, status = 'parsing') => {
    setImportFeedItems(prev => [...prev.slice(-20), { name, status }]);
  }, []);

  // Handle order guide decision
  const processFilesWithAI = async (files) => {
    if (!currentStore) {
      alert('Store information not loaded. Please refresh the page.');
      return;
    }
    if (!files || files.length === 0) {
      alert('Please select at least one file first');
      return;
    }
    const storeId = currentStore.id || currentStore.store_id;

    // Pre-flight size check, before any network call and before the modal opens.
    // A vendor order guide lists everything a restaurant has ever bought; the
    // caps further down this pipeline only apply after the model has read it.
    // ImportExtract counts the sheet in the browser for zero tokens, and a file
    // it cannot count is never refused. See public/js/import-extract.js.
    if (window.ImportExtract && typeof window.ImportExtract.countImportRows === 'function') {
      for (const candidate of files) {
        let verdict = null;
        try {
          verdict = window.ImportExtract.assessImportSize(
            await window.ImportExtract.countImportRows(candidate),
          );
        } catch (err) {
          console.warn('[import] pre-flight size check skipped:', err?.message);
        }
        if (verdict && !verdict.withinLimit) {
          alert(`“${candidate.name}” — ${verdict.message}`);
          return;
        }
      }
    }

    // Clear any prior import-result panel so a fresh upload doesn't render
    // stale "Import complete" data from the previous file.
    setImportResult(null);
    setImportResultErrors([]);
    setOpenResultSection(null);

    // Check subscription / AI import access.
    //
    // gatePage owns this decision — do NOT re-derive status or AI caps here.
    // Getting items in is how a store becomes usable, so it is never metered
    // during setup, and a trial has the same access a setup user does; gatePage
    // also picks the right prompt for everyone else (a free store past setup is
    // offered the trial, a lapsed one is asked to renew). The one ceiling that
    // is not a subscription question is the iOS free tier's 100-item cap — an
    // App Store constraint the DB trigger enforces — so it is checked here.
    if (storeId && window.SubscriptionGate) {
      try {
        const sub = await window.SubscriptionGate.fetchStatus(storeId, true);
        if (sub?.status === 'ios_free' && sub.ios_limit_reached) {
          if (window.PaywallModal?.showIosItemLimit) window.PaywallModal.showIosItemLimit();
          else alert('Item limit reached. Your free account includes up to 100 inventory items. Remove an existing item to add a new one.');
          return;
        }
        const allowed = await window.SubscriptionGate.gatePage?.('ai_import');
        if (allowed === false) return;
      } catch (e) {
        console.warn('[FileUpload] Subscription check error:', e);
      }
    }

    // Reset and show modal
    setImportModalVisible(true);
    setImportClassification(null);
    setImportFeedItems([]);
    setImportStatusText('Analyzing document…');
    setImportProgressPct(0);
    setImportItemsProcessed(0);
    setImportTotalEstimated(0);
    setImportError(null);
    setImportCompletionSummary(null);
    setImportReadyForReview(false);
    setImportSessionSummary(null);
    setImportLastFileCount(0);
    setImportFileResults([]);
    setImportFileProgress(null);
    if (summaryTimerRef.current) { clearTimeout(summaryTimerRef.current); summaryTimerRef.current = null; }
    setDeferredReviewNotice(null);

    setAiProcessing(true);
    setUploading(true);
    setDetailedStatus([]);
    setProgressInfo({ currentChunk: 0, totalChunks: files.length, currentItem: 0, totalItems: 0, percentage: 0 });

    const startedAtIso = new Date().toISOString();
    let totalImportedRows = 0;
    const collectedItemIds = [];
    const storeIdForDedup = currentStore?.id || currentStore?.store_id;

    try {
      // Snapshot catalog before any edge-function writes so dedup compares
      // against the true pre-import state.
      if (storeIdForDedup) {
        try {
          preImportCatalogSnapshotRef.current = await fetchCatalogRowsForStore(
            storeIdForDedup,
            'id,description,vendor,unit,case_price,unit_price'
          );
        } catch (_) {
          preImportCatalogSnapshotRef.current = [];
        }
      } else {
        preImportCatalogSnapshotRef.current = [];
      }

      // Pre-classify the first file to detect document type
      const classification = await classifyDocument(files[0]);
      const cuisine = classification?.cuisine || 'food_general';
      const humorMessage = getHumorMessage(cuisine);
      setImportClassification({ ...classification, cuisine, humorMessage });
      setImportStatusText('Extracting items…');

      // Auto-handle likely long lists: continue processing with item cap and queue support review.
      if (classification && (classification.type === 'order_guide' || classification.large === 'yes')) {
        const preemptiveNotice = {
          required: true,
          item_limit: MAX_ITEMS_PER_DOCUMENT,
          deferred_sources: files.map((file) => ({ source_name: file.name, source_type: 'document' })),
          support_notified: true,
          message: `Large list detected. We will import up to ${MAX_ITEMS_PER_DOCUMENT} items per document, then complete review later.`,
        };
        setDeferredReviewNotice(preemptiveNotice);
        setImportStatusText(`Large list detected — importing first ${MAX_ITEMS_PER_DOCUMENT} items per document`);
        addDetailedStatus(`⚠️ Large list detected. Import is capped at ${MAX_ITEMS_PER_DOCUMENT} items per document and support review is queued automatically.`, 'info');
        addFeedItem(`Large list detected: cap ${MAX_ITEMS_PER_DOCUMENT} items/document`, 'normalizing');
      }

      let lastFileImportedCount = 0;
      const localFileResults = [];
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        if (files.length > 1) {
          setImportFileProgress({ currentIndex: index + 1, totalFiles: files.length, currentFileName: file.name, completedCount: index });
        }
        setImportStatusText(`Processing file ${index + 1} of ${files.length}…`);
        addFeedItem(file.name, 'parsing');

        const fileVendorCollector = {};
        const importedCount = await processSingleFileWithAI(file, index + 1, files.length, collectedItemIds, fileVendorCollector);
        lastFileImportedCount = importedCount;
        totalImportedRows += importedCount;

        if (files.length > 1) {
          setImportFileProgress({ currentIndex: index + 1, totalFiles: files.length, currentFileName: file.name, completedCount: index + 1 });
        }

        // Always record a per-file entry (even when importedCount is 0)
        // so the intermediate "Import complete" hold view in
        // ImportProcessingModal can render a ✓ line per file with vendor
        // detail for both single-file and multi-file imports.
        const vendors = Object.entries(fileVendorCollector)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
        const entry = { fileName: file.name, importedCount, vendors };
        localFileResults.push(entry);
        setImportFileResults([...localFileResults]);

        setImportItemsProcessed(totalImportedRows);
        setImportProgressPct(Math.round(((index + 1) / files.length) * 100));
        setProgressInfo((prev) => ({
          ...prev,
          currentChunk: index + 1,
          currentItem: totalImportedRows,
          totalItems: totalImportedRows,
          percentage: Math.round(((index + 1) / files.length) * 100)
        }));
      }

      if (totalImportedRows === 0) {
        throw new Error('No valid items were detected in the selected files.');
      }

      setImportStatusText('Loading imported items for review…');
      setProcessingStatus(`🔍 Loading ${totalImportedRows} imported rows for review...`);

      // Increment AI import counter for trial tracking
      if (storeId && totalImportedRows > 0 && window.SubscriptionGate) {
        try {
          await window.SubscriptionGate.incrementAiImported(storeId, totalImportedRows);
        } catch (e) {
          console.warn('[FileUpload] incrementAiImported error:', e);
        }
      }

      const importedRows = await fetchRecentImportedItems(startedAtIso, totalImportedRows, collectedItemIds);
      if (!importedRows.length) {
        throw new Error('Imported rows could not be loaded for review.');
      }

      // ── Within-batch dedup ────────────────────────────────────────────
      // If the same item appears across multiple invoices in this batch,
      // keep only the first occurrence. Reorder by insertion order
      // (collectedItemIds) so "first invoice wins"; fetchRecentImportedItems
      // returns rows newest-first, which would otherwise reverse the rule.
      // Invoice dates aren't read here, so we deliberately do NOT update
      // prices for the duplicates we drop.
      const insertionOrder = new Map();
      collectedItemIds.forEach((id, idx) => {
        const n = Number(id);
        if (!Number.isNaN(n) && !insertionOrder.has(n)) insertionOrder.set(n, idx);
      });
      const orderedImportedRows = [...importedRows].sort((a, b) => {
        const ai = insertionOrder.has(Number(a.id)) ? insertionOrder.get(Number(a.id)) : Number.MAX_SAFE_INTEGER;
        const bi = insertionOrder.has(Number(b.id)) ? insertionOrder.get(Number(b.id)) : Number.MAX_SAFE_INTEGER;
        return ai - bi;
      });

      const { kept: dedupedRows, dropped: droppedDuplicates } = dedupeBatchItems(orderedImportedRows);

      if (droppedDuplicates.length > 0) {
        const droppedIds = droppedDuplicates
          .map(({ item }) => Number(item?.id))
          .filter((id) => !Number.isNaN(id));
        if (droppedIds.length > 0 && storeIdForDedup) {
          try {
            const { error: deleteError } = await supabase
              .from('inventory_items')
              .delete()
              .eq('store_id', storeIdForDedup)
              .in('id', droppedIds);
            if (deleteError) {
              console.warn('[FileUpload] Failed to delete duplicate batch rows:', deleteError);
            } else {
              totalImportedRows = Math.max(0, totalImportedRows - droppedIds.length);
            }
          } catch (e) {
            console.warn('[FileUpload] Error deleting duplicate batch rows:', e);
          }
        }
        addDetailedStatus(
          `ℹ️ Skipped ${droppedDuplicates.length} duplicate ${droppedDuplicates.length === 1 ? 'item' : 'items'} that already appeared in this import.`,
          'info'
        );
      }

      // ── Dedup classification ──────────────────────────────────────────
      setImportStatusText('Classifying items against your catalog…');
      const existingCatalog = preImportCatalogSnapshotRef.current || [];

      const classifiedItems = classifyImportItems(dedupedRows, existingCatalog);

      // ── Count target pre-loading for new items ────────────────────────
      const newItemsList = classifiedItems.filter((i) => i._classification === 'new');
      const targetsMap = {};
      if (newItemsList.length > 0 && storeIdForDedup) {
        for (const item of newItemsList) {
          try {
            const targets = await findCountTargetsForNewItem({
              storeId: storeIdForDedup,
              itemCategory: item.category,
              itemVendor: item.vendor,
              inventoryItemId: item.id,
              itemSnapshot: item,
            });
            targetsMap[item.id] = targets.map((t) => ({ ...t, selected: !t.alreadyPresent }));
          } catch (_) {
            targetsMap[item.id] = [];
          }
        }
      }

      setCountTargetsMap(targetsMap);
      setProcessedItems(classifiedItems);
      setSelectedItemIds(new Set(classifiedItems.map((item) => item.id)));
      setFileName(files.map((file) => file.name).join(', '));
      setProcessingStatus(`🎉 ${classifiedItems.length} imported rows ready for review.`);
      // Show "import complete, preparing summary" state (no button) for ~5s,
      // then auto-advance to the session summary modal view.
      setImportLastFileCount(lastFileImportedCount);
      setImportReadyForReview(true);
      if (summaryTimerRef.current) clearTimeout(summaryTimerRef.current);
      summaryTimerRef.current = setTimeout(() => {
        summaryTimerRef.current = null;
        const sourceVendors = new Set();
        localFileResults.forEach((entry) => (entry.vendors || []).forEach((vendor) => {
          const name = String(vendor?.name || '').trim();
          if (name) sourceVendors.add(name);
        }));
        const totalVendors = sourceVendors.size || new Set(
          classifiedItems.map(i => (i.vendor || '').trim()).filter(Boolean)
        ).size;
        // Summary cardinality is the source batch, never the number of
        // canonical catalog identities that happen to survive matching.
        setImportSessionSummary({ totalItems: totalImportedRows, totalVendors, fileResults: localFileResults });
      }, 5000);
    } catch (error) {
      console.error('❌ Import error:', error);
      setProcessingStatus('❌ Import failed');
      setImportError(error.message);
      setImportCompletionSummary({ imported: totalImportedRows, errors: 1, duplicates: 0 });
      alert('Error importing file: ' + formatErrorMessage(error, 'Import failed.'));
    } finally {
      setAiProcessing(false);
      setUploading(false);
    }
  };

  // Finalize import — remove deselected items
  const saveReviewedItems = async () => {
    if (!currentStore || !processedItems.length) return;

    setAiProcessing(true);
    setProcessingStatus('💾 Finalizing import...');

    try {
      const storeId = currentStore.id || currentStore.store_id;

      // Re-run dedup at save-time using the pre-import catalog snapshot.
      const snapshotCatalog = preImportCatalogSnapshotRef.current || [];
      const snapshotCatalogById = new Map(snapshotCatalog.map((r) => [String(r.id), r]));

      const reclassifiedItems = classifyImportItems(
        processedItems.map((item) => ({
          // Use current (possibly edited) field values for the dedup key
          ...item,
          description: item.description,
          vendor: item.vendor,
          unit: item.unit,
          case_price: item.case_price,
        })),
        snapshotCatalog
      ).map((item) => {
        // Use snapshot existing item (captures pre-import values for history).
        const snapshotExisting = item._existingItem
          ? (snapshotCatalogById.get(String(item._existingItem.id)) || item._existingItem)
          : null;
        // Honor a user-driven override; otherwise use the fresh dedup result.
        const finalClass = item._classificationOverride || item._classification;
        return { ...item, _existingItem: snapshotExisting, _classification: finalClass };
      });

      // Confirm any override-induced duplicates before proceeding.
      const duplicateOverrides = reclassifiedItems.filter(
        (i) => i._classificationOverride === 'new' && i._existingItem
      );
      if (duplicateOverrides.length > 0) {
        const lines = duplicateOverrides
          .slice(0, 10)
          .map((i) => `  • ${i.description || '(unnamed)'} — duplicates "${i._existingItem.description}"`)
          .join('\n');
        const more = duplicateOverrides.length > 10 ? `\n  …and ${duplicateOverrides.length - 10} more` : '';
        const ok = window.confirm(
          `${duplicateOverrides.length} item${duplicateOverrides.length === 1 ? '' : 's'} will be saved as NEW even though a matching catalog item already exists. This will create duplicate rows in your catalog:\n\n${lines}${more}\n\nContinue?`
        );
        if (!ok) {
          setAiProcessing(false);
          setProcessingStatus('');
          return;
        }
      }

      // Separate by final reclassified result
      const unchangedItems  = reclassifiedItems.filter((i) => i._classification === 'unchanged');
      const priceChangeItems = reclassifiedItems.filter((i) => i._classification === 'price_change');
      const newItemsList    = reclassifiedItems.filter((i) => i._classification === 'new');
      // Disapproved in review. Unlike every other bucket this one has to
      // UNDO work: the edge function already inserted these rows, so leaving
      // them alone would make "Don't import" a lie. Only ids from this batch
      // are touched (preview placeholders have no row to delete), and the
      // delete is scoped to the store.
      const rejectedItems   = reclassifiedItems.filter((i) => i._classification === 'rejected');

      const skippedDetails = unchangedItems.map((i) => ({
        description: i.description || i._existingItem?.description || '(unnamed)',
        vendor: i.vendor || i._existingItem?.vendor || '',
        unit: i.unit || i._existingItem?.unit || '',
        case_price: parseFloat(i.case_price) || parseFloat(i._existingItem?.case_price) || null,
        fuzzy: !!i._fuzzyMatched,
      }));
      const updatedDetails = [];
      const addedDetails = [];
      const importErrors = [];

      // ── UNCHANGED: no-op (edge function already upserted correctly) ──
      const unchangedCount = unchangedItems.length;

      // ── PRICE CHANGE: write price_history row (edge function already updated row) ──
      let priceUpdated = 0;
      for (const item of priceChangeItems) {
        const existing = item._existingItem;
        if (!existing?.id) continue;
        const newPrice = parseFloat(item.case_price);
        if (!Number.isFinite(newPrice)) continue;

        await recordPriceHistory({
          inventoryItemId: existing.id,
          storeId,
          oldName: existing.description,
          newName: item.description || existing.description,
          oldCasePrice: existing.case_price,
          newCasePrice: newPrice,
          oldUnitPrice: existing.unit_price,
          newUnitPrice: existing.unit_price != null ? newPrice : null,
          source: 'import_approval',
          invoiceVendor: item.vendor || null,
          invoiceUnit: item.unit || null,
          priceDeltaAbsolute: item._priceDeltaAbsolute,
          priceDeltaPct: item._priceDeltaPct,
        });

        priceUpdated++;
        updatedDetails.push({
          description: item.description || existing.description || '(unnamed)',
          vendor: item.vendor || existing.vendor || '',
          unit: item.unit || existing.unit || '',
          oldPrice: parseFloat(existing.case_price),
          newPrice,
          deltaAbs: item._priceDeltaAbsolute,
          deltaPct: item._priceDeltaPct,
        });
      }

      // ── DISAPPROVED: remove the rows the edge function inserted ──
      let rejectedCount = 0;
      const rejectedIds = rejectedItems
        .map((i) => i.id)
        .filter((id) => id != null && !String(id).startsWith('preview-'));
      if (rejectedIds.length > 0) {
        try {
          const { error: deleteError } = await supabase
            .from('inventory_items')
            .delete()
            .in('id', rejectedIds)
            .eq('store_id', storeId);
          if (deleteError) throw deleteError;
          rejectedCount = rejectedIds.length;
        } catch (deleteErr) {
          importErrors.push(`Could not remove ${rejectedIds.length} disapproved item(s): ${deleteErr.message}`);
        }
      }

      // ── NEW ITEMS: no-op for inventory rows (edge function already inserted) ──
      const resolvedNewItems = newItemsList;
      for (const item of resolvedNewItems) {
        addedDetails.push({
          description: String(item.description || '').trim() || 'Unknown Item',
          vendor: normalizeVendorName(item.vendor) || item.vendor || '',
          unit: item.unit || 'each',
          case_price: parseFloat(item.case_price) || null,
        });
      }

      // Log price history for new items inserted by the edge function with a non-null price
      for (const item of resolvedNewItems) {
        const newPrice = parseFloat(item.case_price);
        if (!Number.isFinite(newPrice) || newPrice <= 0 || !item.id) continue;
        await recordPriceHistory({
          inventoryItemId: item.id,
          storeId,
          oldName: null,
          newName: String(item.description || '').trim() || null,
          oldCasePrice: null,
          newCasePrice: newPrice,
          oldUnitPrice: null,
          newUnitPrice: null,
          source: 'import_approval',
          invoiceVendor: item.vendor || null,
          invoiceUnit: item.unit || null,
        });
      }

      // Auto-assign new items to selected in-progress count sessions
      for (const item of resolvedNewItems) {
        const targets = (countTargetsMap[item.id] || []).filter((t) => t.selected);
        if (!targets.length) continue;
        const snapshot = {
          id: item.id,
          description: item.description,
          category: item.category,
          location: item.location,
          vendor: item.vendor,
          unit: item.unit,
          case_price: parseFloat(item.case_price) || null,
          case_quantity: parseInt(item.case_quantity, 10) || null,
        };
        await assignItemToCountSessions({
          storeId,
          inventoryItemId: item.id,
          itemSnapshot: snapshot,
          selectedSessions: targets.map((t) => t.session),
        });
      }

      const keptCount = resolvedNewItems.length;
      setProcessingStatus(
        `🎉 Import complete! ${keptCount} new, ${priceUpdated} updated, ${unchangedCount} unchanged (skipped)`
        + `${rejectedCount > 0 ? `, ${rejectedCount} disapproved (removed)` : ''}.`,
      );
      setImportResult({
        added: keptCount,
        updated: priceUpdated,
        ignored: unchangedCount,
        rejected: rejectedCount,
        addedDetails,
        updatedDetails,
        skippedDetails,
      });
      setImportResultErrors(importErrors);
      setOpenResultSection(null);

      setShowPreview(false);
      setProcessedItems([]);
      setSelectedItemIds(new Set());
      setCountTargetsMap({});

      if (onUpload) {
        const finalizedFileName = fileName;
        setFileName('');
        // Collect the catalog IDs touched by this import so the host page can
        // offer to build count/ordering templates scoped to just this batch.
        // New rows carry their freshly-inserted id; matched (price-change /
        // unchanged) rows point at the existing catalog row via _existingItem.
        const importedItemIds = Array.from(new Set([
          ...resolvedNewItems.map((i) => i.id),
          ...priceChangeItems.map((i) => i._existingItem?.id),
          ...unchangedItems.map((i) => i._existingItem?.id),
        ].filter(Boolean)));
        onUpload({ name: finalizedFileName, itemIds: importedItemIds });
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      setProcessingStatus('❌ Error finalizing');
      alert('Error: ' + formatErrorMessage(error, 'Finalize failed.'));
    } finally {
      setAiProcessing(false);
      setTimeout(() => { setProcessingStatus(''); }, 3000);
    }
  };

  // Go back to upload interface
  const goBackToUpload = () => {
    setShowPreview(false);
    setProcessedItems([]);
    setCountTargetsMap({});
    setFileName('');
    setProcessingStatus('');
    clearSelectedFiles();
  };

  // Update item in preview (recompute price deltas if case_price changes)
  const updatePreviewItem = (itemId, field, value) => {
    setProcessedItems(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;
        const updated = { ...item, [field]: value };
        // Skip automatic classification flip if the user has manually overridden it.
        if (field === 'case_price' && item._existingItem && !item._classificationOverride) {
          const oldP = parseFloat(item._existingItem.case_price);
          const newP = parseFloat(value);
          if (Number.isFinite(oldP) && Number.isFinite(newP)) {
            const samePrice = Math.abs(newP - oldP) < 0.001;
            if (samePrice) {
              updated._classification = 'unchanged';
              updated._priceDeltaAbsolute = null;
              updated._priceDeltaPct = null;
            } else {
              updated._classification = 'price_change';
              updated._priceDeltaAbsolute = +(newP - oldP).toFixed(4);
              updated._priceDeltaPct = oldP !== 0 ? +((newP - oldP) / Math.abs(oldP) * 100).toFixed(2) : null;
            }
          }
        }
        return updated;
      })
    );
  };

  // Remove item from preview
  const removePreviewItem = (itemId) => {
    setProcessedItems(prev => prev.filter(item => item.id !== itemId));
  };

  // Take photo = the shared document scanner (live camera, edge detection,
  // review). Confirmed pages join the same selection as picked files. The
  // native capture input stays as fallback.
  const openCameraPicker = () => {
    void takeScanPhoto({
      destinationKind: 'item_import',
      stem: 'item',
      onFiles: (files) => {
        addFilesToSelection(files);
        files.forEach((file) => addDetailedStatus(`📷 Scanned ${file.name}`, 'info'));
      },
      fallback: () => {
        if (cameraInputRef.current) cameraInputRef.current.click();
      },
    });
  };

  const handleCameraCapture = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      addFilesToSelection([file]);
      addDetailedStatus(`📷 Loaded ${file.name}`, 'info');
    } catch (error) {
      console.error('Camera file add error:', error);
      alert('Could not load selected photo. Please try again.');
    } finally {
      event.target.value = '';
    }
  };

  // Modal element (rendered in all states, visibility controlled by prop)
  const importModal = (
    <ImportProcessingModal
      visible={importModalVisible}
      classification={importClassification}
      feedItems={importFeedItems}
      statusText={importStatusText}
      progressPct={importProgressPct}
      itemsProcessed={importItemsProcessed}
      totalEstimated={importTotalEstimated}
      lastFileItemsCount={importLastFileCount}
      fileResults={importFileResults}
      fileProgress={importFileProgress}
      error={importError}
      completionSummary={importCompletionSummary}
      readyForReview={importReadyForReview}
      sessionSummary={importSessionSummary}
      showOrderGuideWarning={showOrderGuideWarning}
      onDismiss={() => {
        // Only used by the error state Close button
        setImportModalVisible(false);
        setImportReadyForReview(false);
        setImportSessionSummary(null);
        if (summaryTimerRef.current) { clearTimeout(summaryTimerRef.current); summaryTimerRef.current = null; }
      }}
      onNext={() => {
        // Session summary "Next" button — hide modal and show the review table
        setImportModalVisible(false);
        setImportReadyForReview(false);
        setImportSessionSummary(null);
        setShowPreview(true);
      }}
      onContinueAnyway={() => {}}
      onSendToSupport={() => {}}
    />
  );

  if (showPreview) {
    return (
      <div className="preview-results">
        {importModal}

        {deferredReviewNotice?.required && (
          <div className="alert alert-warning mb-3" style={{ background: '#fff7e6', color: '#7a5a2a', borderRadius: 8 }}>
            <strong>Large list detected.</strong> {deferredReviewNotice.message || `We imported up to ${MAX_ITEMS_PER_DOCUMENT} items and queued support review for the rest.`}
          </div>
        )}

        <div style={{ marginBottom: '0.75rem' }}>
          <h6 style={{ color: '#5A4A42', fontWeight: 700, marginBottom: 2 }}>Review imported items</h6>
          <p style={{ color: '#9a8a82', fontSize: '0.85rem', margin: 0 }}>
            Edit fields inline, group-select by vendor, then click Import to save your selections.
          </p>
        </div>

        <ImportApprovalTable
          items={processedItems}
          onUpdateItem={updatePreviewItem}
          onReclassifyItem={(itemId, newClassification) => {
            setProcessedItems((prev) =>
              prev.map((it) => (it.id === itemId ? overrideItemClassification(it, newClassification) : it))
            );
          }}
          onImport={saveReviewedItems}
          onBack={aiProcessing ? undefined : goBackToUpload}
          isImporting={aiProcessing}
          storeId={currentStore?.id || currentStore?.store_id}
          countTargetsMap={countTargetsMap}
          onToggleCountTarget={(itemId, sessionId) => {
            setCountTargetsMap((prev) => {
              const targets = (prev[itemId] || []).map((t) =>
                t.session.id === sessionId ? { ...t, selected: !t.selected } : t
              );
              return { ...prev, [itemId]: targets };
            });
          }}
        />

        {processingStatus && (
          <div className="alert alert-info mt-3">
            {processingStatus}
          </div>
        )}
      </div>
    );
  }

  const fmtMoney = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
  };
  const fmtPct = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return '';
    const sign = n > 0 ? '+' : '';
    return ` (${sign}${n.toFixed(1)}%)`;
  };

  const renderResultSection = ({ id, label, count, color, bg, border, items, render }) => {
    const isOpen = openResultSection === id;
    const disabled = !count;
    return (
      <div style={{ marginTop: '0.6rem' }} key={id}>
        <button
          type="button"
          onClick={() => !disabled && setOpenResultSection(isOpen ? null : id)}
          disabled={disabled}
          style={{
            width: '100%', textAlign: 'left', cursor: disabled ? 'default' : 'pointer',
            background: bg, border: `1px solid ${border}`, borderRadius: 8,
            padding: '0.55rem 0.75rem', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', color, fontWeight: 700, fontSize: '0.9rem',
            opacity: disabled ? 0.55 : 1,
          }}
        >
          <span>{label} — {count}</span>
          {!disabled && <span style={{ fontSize: '0.85rem' }}>{isOpen ? '▾' : '▸'}</span>}
        </button>
        {isOpen && count > 0 && (
          <div style={{
            border: `1px solid ${border}`, borderTop: 'none',
            borderRadius: '0 0 8px 8px', background: '#fff',
            padding: '0.5rem 0.75rem', maxHeight: 220, overflowY: 'auto',
          }}>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.85rem', color: '#3F352E' }}>
              {items.map((it, i) => (
                <li key={i} style={{
                  padding: '0.3rem 0',
                  borderBottom: i < items.length - 1 ? '1px dashed #EEE6DC' : 'none',
                }}>
                  {render(it)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const importResultPanel = importResult ? (
    <div style={{
      background: '#FFFDF9', border: '1px solid #E8DBCE', borderRadius: 12,
      padding: '0.9rem 1rem', marginBottom: '1rem', color: '#3F352E',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
        <div>
          <div style={{ fontWeight: 800, fontFamily: "'Montserrat', sans-serif", fontSize: '1.05rem', color: '#5A4A42' }}>
            ✓ Import complete
          </div>
          <div style={{ marginTop: '0.2rem', fontSize: '0.85rem', color: '#7A6A60' }}>
            Processed {(importResult.added || 0) + (importResult.updated || 0) + (importResult.ignored || 0)} item(s).
            Click any section to see exactly what changed.
          </div>
        </div>
        <button onClick={() => { setImportResult(null); setImportResultErrors([]); setProcessingStatus(''); }} style={{
          background: '#fff', border: '1px solid #E8DBCE', borderRadius: 8,
          padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 700,
          color: '#5A4A42', cursor: 'pointer',
        }}>Done</button>
      </div>

      {renderResultSection({
        id: 'updated', label: 'Price updates',
        count: importResult.updated, color: '#8B6135',
        bg: '#FCF4EA', border: '#E8CEAF',
        items: importResult.updatedDetails || [],
        render: (it) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ fontWeight: 600 }}>{it.description}</span>
              {(it.vendor || it.unit) && (
                <span style={{ color: '#7A6A60', fontSize: '0.8rem', marginLeft: '0.4rem' }}>
                  {[it.vendor, it.unit].filter(Boolean).join(' · ')}
                </span>
              )}
            </span>
            <span style={{ color: '#8B6135', whiteSpace: 'nowrap' }}>
              {fmtMoney(it.oldPrice)} → {fmtMoney(it.newPrice)}{fmtPct(it.deltaPct)}
            </span>
          </div>
        ),
      })}
      {renderResultSection({
        id: 'added', label: 'New items added',
        count: importResult.added, color: '#456A2F',
        bg: '#F1F8EE', border: '#CFE3BE',
        items: importResult.addedDetails || [],
        render: (it) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{it.description}</span>
            <span style={{ color: '#7A6A60', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
              {[it.vendor, it.unit].filter(Boolean).join(' · ')}{it.case_price != null ? ` · ${fmtMoney(it.case_price)}` : ''}
            </span>
          </div>
        ),
      })}
      {renderResultSection({
        id: 'skipped', label: 'Skipped (already in catalog, no change)',
        count: importResult.ignored, color: '#5A4A42',
        bg: '#F4EFE7', border: '#E1D4C2',
        items: importResult.skippedDetails || [],
        render: (it) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span>
              <span style={{ fontWeight: 600 }}>{it.description}</span>
              {it.fuzzy && (
                <span style={{
                  marginLeft: '0.4rem', fontSize: '0.7rem', fontWeight: 700,
                  color: '#8B6135', background: '#FCF4EA',
                  border: '1px solid #E8CEAF', borderRadius: 4, padding: '1px 5px',
                }}>fuzzy match</span>
              )}
            </span>
            <span style={{ color: '#7A6A60', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
              {[it.vendor, it.unit].filter(Boolean).join(' · ')}
            </span>
          </div>
        ),
      })}

      {importResultErrors && importResultErrors.length > 0 && (
        <div style={{
          marginTop: '0.7rem', background: '#FEEAEA', border: '1px solid #F4C2C2',
          borderRadius: 8, padding: '0.5rem 0.75rem', color: '#A03030', fontSize: '0.85rem',
        }}>
          <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{importResultErrors.length} error(s):</div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {importResultErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="file-upload">
      {importModal}
      {importResultPanel}
      <style>
        {`
          .file-upload .upload-area {
            border: 2px dashed #aabbc8;
            border-radius: 12px;
            padding: 1.2rem;
            background: #fcfdfe;
            transition: all 0.2s ease;
            cursor: pointer;
          }
          .file-upload .upload-area:hover,
          .file-upload .upload-area.dragover {
            border-color: #7f9ab0;
            background: #f4f8fc;
          }
          .file-upload .upload-hero {
            text-align: center;
            margin-bottom: 0.75rem;
          }
          .file-upload .upload-icon {
            color: #5A4A42;
            margin-bottom: 0.8rem;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            width: 3rem;
            height: 3rem;
            background: #eef3f8;
            border-radius: 999px;
            font-size: 1.35rem;
          }
          .file-upload .upload-hero-title {
            color: #5A4A42;
            font-size: 1rem;
            font-weight: 700;
            margin-bottom: 0.2rem;
          }
          .file-upload .upload-hero-subtitle {
            color: #8B7B73;
            font-size: 0.85rem;
            margin: 0;
          }
          @media (max-width: 576px) {
            .file-upload .upload-camera-btn {
              order: -1;
              font-size: 0.9rem;
              padding: 0.45rem 1rem;
            }
            .file-upload .upload-browse-btn {
              order: 0;
            }
          }
          .file-upload .file-info {
            background: #f8f6f4;
            border: 1px solid #e7ded6;
            border-radius: 10px;
            padding: 0.9rem;
            margin: 1rem 0;
          }
          .file-upload .file-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.45rem 0.55rem;
            background: #fff;
            border-radius: 8px;
          }
          body.items-import-page-locked {
            overflow: hidden !important;
          }
          body.items-import-page-locked .inventory-manager > *:not(.import-modal-overlay) {
            pointer-events: none;
          }
          body.items-import-page-locked .import-modal-overlay,
          body.items-import-page-locked .import-modal-overlay * {
            pointer-events: auto;
          }
        `}
      </style>

      <input
        ref={fileInputRef}
        type="file"
        accept={allowedFileExtensions.join(',')}
        multiple
        onChange={handleFileInputChange}
        style={visuallyHiddenFileInputStyle}
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* AI Processing Status with Progress Meter */}
      {aiProcessing && (
        <div className="alert alert-info mb-4">
          <div className="d-flex align-items-center mb-3">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            <span className="ai-dot" style={{marginRight:'8px'}} />
            <span className="fw-bold">{processingStatus || 'AI is processing your file...'}</span>
          </div>
          
          {/* Progress Bar */}
          {progressInfo.totalChunks > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">
                  Processing chunk {progressInfo.currentChunk}/{progressInfo.totalChunks}
                </span>
                <span className="small text-muted">
                  {progressInfo.percentage}%
                </span>
              </div>
              <div className="progress" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-primary" 
                  role="progressbar" 
                  style={{ width: `${progressInfo.percentage}%` }}
                  aria-valuenow={progressInfo.percentage} 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                ></div>
              </div>
              {progressInfo.totalItems > 0 && (
                <div className="small text-muted mt-1">
                  Found {progressInfo.totalItems} items so far...
                </div>
              )}
            </div>
          )}
          
          <div className="small text-muted">
            This may take 10-30 seconds depending on file size
          </div>
        </div>
      )}

      {/* Detailed Processing Status */}
      {aiProcessing && detailedStatus.length > 0 && (
        <div className="card mb-3" style={{ border: '1px solid #eadfd2' }}>
          <div className="card-header py-2">
            <h6 className="mb-0">📊 Processing Details</h6>
          </div>
          <div className="card-body py-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
            {detailedStatus.map((status, index) => (
              <div key={index} className={`small mb-1 ${status.type === 'error' ? 'text-danger' : status.type === 'success' ? 'text-success' : 'text-info'}`}>
                <span className="text-muted me-2">{status.timestamp}</span>
                {status.message}
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        className={`upload-area mb-3 ${isDragOver ? 'dragover' : ''}`}
        onClick={() => {
          if (!aiProcessing && !uploading) fileInputRef.current?.click();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!aiProcessing && !uploading) setIsDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          if (aiProcessing || uploading) return;
          setIsDragOver(false);
          addFilesToSelection(event.dataTransfer?.files);
        }}
      >
        <div className="upload-hero">
          <div className="upload-icon">📄</div>
          <div className="upload-hero-title">Drop your files here</div>
          <p className="upload-hero-subtitle">
            {isCameraMode
              ? 'Photos or PDFs • up to 10 files'
              : 'CSV, Excel, PDF, text, or images (PNG/JPG/HEIC) • up to 20 files'}
          </p>
          <div className="d-flex flex-wrap gap-2 justify-content-center mt-2 upload-action-row">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm upload-browse-btn"
              onClick={(event) => {
                event.stopPropagation();
                if (!aiProcessing && !uploading) fileInputRef.current?.click();
              }}
              disabled={aiProcessing || uploading}
            >
              Browse files
            </button>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm upload-camera-btn"
              onClick={(event) => {
                event.stopPropagation();
                if (!aiProcessing && !uploading) openCameraPicker();
              }}
              disabled={aiProcessing || uploading}
            >
              <i className="feather icon-camera me-1"></i>
              Take photo
            </button>
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*,.pdf,.heic,.heif"
            capture="environment"
            onChange={handleCameraCapture}
            style={visuallyHiddenFileInputStyle}
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <div className="file-info">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div>
              <strong>{selectedFiles.length} file{selectedFiles.length === 1 ? '' : 's'} selected</strong>
              <div className="small text-muted">
                {formatFileSize(selectedFiles.reduce((total, file) => total + (file.size || 0), 0))} total
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={clearSelectedFiles}
              disabled={aiProcessing || uploading}
            >
              Clear all
            </button>
          </div>
          <div style={{ display: 'grid', gap: '0.35rem' }}>
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${file.size}-${index}`}>
                <div className="file-row">
                  <span style={{ fontSize: '0.95rem' }}>📄</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#5A4A42', fontWeight: 600, fontSize: '0.85rem' }}>
                    {file.name}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#8B7B73' }}>{formatFileSize(file.size || 0)}</span>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-danger"
                    style={{ fontSize: '0.95rem', lineHeight: 1 }}
                    onClick={() => removeSelectedFile(index)}
                    disabled={aiProcessing || uploading}
                    aria-label={`Remove ${file.name}`}
                  >
                    ✕
                  </button>
                </div>
                <StagedFilePreview file={file} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <button
          className="btn btn-link text-decoration-none px-0"
          onClick={onCancel}
          disabled={uploading}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleManualUpload}
          disabled={uploading || aiProcessing || selectedFiles.length === 0}
        >
          {aiProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Processing...
            </>
          ) : uploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Uploading...
            </>
          ) : (
            <>
              <i className="feather icon-upload me-1"></i>
              Import files
            </>
          )}
        </button>
      </div>

      {processingStatus && !aiProcessing && (
        <div className="alert alert-info mt-3 mb-0">
          {processingStatus}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
